import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { AppBar, Button, Chip, Grid, IconButton, LinearProgress, Switch, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { makeStyles } from '@mui/styles';
import DOMPurify from 'isomorphic-dompurify';
import * as React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { CREATE, DataAPIContext, GET_ONE, GET_LIST } from '../api';
import AccessDeniedView from "../components/AccessDeniedView";
import ConfirmationDialog from "../components/ConfirmationDialog";
import images from "../components/images";
import PageMeta from "../components/PageMeta";
import sharedContext from "../sharedContext";
import { ContextStore } from '../store/contextStore';
import ChatListMenu, { MSG_ACTIVITY_ANY, MSG_ACTIVITY_HAS, MSG_ACTIVITY_NONE } from './ChatListMenu'
import DateFilterPopover, { activeDuringLabel, activeDuringParams } from "./components/DateFilterPopover";
import RecordList from "./components/RecordList";
import ConversationsList from './ConversationsList';
//import { ReactComponent as AppSupportIcon } from '@iconify/react'
import { ReactComponent as AppSupportIcon } from './icons/cell_phone.svg';
import { ReactComponent as GroupTourCoverPlaceholder } from './icons/group_tour_cover_placeholder.svg';
import { ReactComponent as MainOfficeIcon } from './icons/main_office.svg';
import { ReactComponent as WebChatIcon } from './icons/web_chat.svg';
import { ReactComponent as MarkReadIcon } from './icons/mark_read.svg';
import { ReactComponent as NotificationBellOffGray } from './icons/notification_bell_off_gray_new.svg';
import { ReactComponent as PauseAutoCommunicationsGray } from './icons/pause_notifications_gray.svg';
import { ReactComponent as IssuesClosedIcon } from './icons/issues_closed.svg';
import { ReactComponent as IssuesAutoClosedIcon } from './icons/issues_auto_closed.svg';
import { ReactComponent as SystemAutoReplyIcon } from './icons/last_message_auto_replied.svg';
// The web-widget handoff badge is the chat header's stopped-chatbot dot, so red means the same thing in the list and the header.
import { ReactComponent as ChatbotHandoffIcon } from './icons/chatbot_settings_red_dot.svg';
import { ReactComponent as SystemAutoReplyAutoClosedIcon } from './icons/issues_auto_closed_auto_replied.svg';
import { getTwilioClient } from './utils/chat_utils'
import { unarchive_channel, formatTourTagsTooltip } from './utils/chat_utils';
import { urlDefinesFilters } from './utils/chat_url_filters';
import { mobileChatPaneHeight } from '../layout/visualViewport';
import { Tag, chatColors } from './components/Tag';
import { cv } from '../themeVars';
import { ChatThemeMenuButton, useChatTheme } from './ChatThemeScope';
import { OverflowTooltip } from './components/OverflowTooltip';
import { staggeredCheckoutText } from './checkoutLabel';
import { tourDateRangeText } from './tourDateRange';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

function migrateCachedMessageActivityFilter(v) {
    if (v === MSG_ACTIVITY_HAS || v === MSG_ACTIVITY_NONE || v === MSG_ACTIVITY_ANY) return v;
    if (v === true) return MSG_ACTIVITY_HAS;
    return MSG_ACTIVITY_ANY;
}

function initMessageActivityTriState(urlVal, cachedVal) {
    if (urlVal === MSG_ACTIVITY_HAS || urlVal === MSG_ACTIVITY_NONE) return urlVal;
    return migrateCachedMessageActivityFilter(cachedVal);
}

function useIsWidthUp(breakpoint) {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.up(breakpoint));
}

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: 0,
        margin: theme.spacing(2),
        //marginTop: 15,
        height: "86svh",
        //width: "88svw",
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            margin: theme.spacing(1),
            overflow: 'hidden',
            flexDirection: 'column',
            minHeight: 0,
        },
        //borderRadius: 10,
        //border: "1px solid rgb(224, 224, 224)",
        //minWidth: "1400px"
        //flexWrap: "wrap"
    },
    gridRoot: {
        borderRadius: 0,
    },
    recordList: {
        '&.MuiDataGrid-root': {
            borderRadius: 0,
            borderTop: `0px solid ${cv('border', 'rgb(224, 224, 224)')}`,
            height: "100%"
        },
        //marginTop: theme.spacing(2),
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            overflowY: 'scroll',
            height: '100%',
        },
        '&.MuiDataGrid-root .MuiDataGrid-cell': {
            borderBottom: 0,
        },
    },
}));

const CHAT_LIST_ROWS_PER_PAGE_LS_PREFIX = 'chatList_rowsPerPage_';

function normalizeChatListRowsPerPage(value, allowed) {
    const n = typeof value === 'number' && Number.isFinite(value) ? value : Number(value);
    return Number.isFinite(n) && allowed.includes(n) ? n : null;
}

function readChatListRowsPerPageLs(userID, allowed) {
    if (userID == null || userID === '' || userID === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(CHAT_LIST_ROWS_PER_PAGE_LS_PREFIX + userID);
        if (raw == null || raw === '') return null;
        return normalizeChatListRowsPerPage(JSON.parse(raw), allowed);
    } catch {
        return null;
    }
}

function writeChatListRowsPerPageLs(userID, perPage) {
    if (userID == null || userID === '' || userID === 'undefined') return;
    try {
        window.localStorage.setItem(CHAT_LIST_ROWS_PER_PAGE_LS_PREFIX + userID, JSON.stringify(perPage));
    } catch {
        /* ignore quota / private mode */
    }
}

const ChatList = (props) => {
    const history = useHistory();
    const location = useLocation();
    const routeParams = useParams();
    let routeParamsChannelID = React.useRef(routeParams.id || null);
    let routeParamsConversationUniqueName = React.useRef(routeParams.twilio_conversation_unique_name || null);
    const dataAPI = React.useContext(DataAPIContext);
    const [lastUpdate,] = React.useState(null);
    const [relatedLoading, setRelatedLoading] = React.useState(false);
    const [query,] = React.useState(null);
    // The Ventrata side-panel extension hands an order over as ?order_ref= when it opens the
    // chat in a normal tab. Seeding the search from the URL here is what lets RecordList refuse
    // scripted input in those tabs without breaking that handoff — the term arrives as state,
    // not as a content script typing into the box. A search carried back from a conversation
    // (location.state) is the more recent intent and still wins.
    const orderRefFromUrl = new URLSearchParams(location?.search || "").get("order_ref")?.trim() || "";
    const [searchText, setSearchText] = React.useState(location?.state?.ChatListSearchText || orderRefFromUrl);
    // "Active during" date filter: { range: [start, end], includeLastDay }. Not cached or
    // put in share URLs -- a remembered date range goes stale by the next day.
    const [activeDuring, setActiveDuring] = React.useState(null);
    const [dateSearchText, setDateSearchText] = React.useState("");
    const [chatListRowTotal, setChatListRowTotal] = React.useState(0);
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [hasUnreadMessages, setHasUnreadMessages] = React.useState(sharedContext.get('unreadMsgCount') > 0)
    const [listRefreshKey, setListRefreshKey] = React.useState(0);

    const bootstrapUser = JSON.parse(window.localStorage.getItem('bootstrap'))?.user || {};
    // Handoffs only exist in web-widget conversations: the "Needs a person" filter is
    // noise for operators without a widget, so the menu renders it only where one exists.
    const operatorConfig = JSON.parse(window.localStorage.getItem('bootstrap'))?.tour_operator?.config || {};
    // The raw flag on purpose, not isWebWidgetAvailable(): unset now means available, which
    // would put the filter on every operator. Explicitly onboarded or live widgets only.
    const hasWebWidget = !!(operatorConfig.web_chat_widget_available || operatorConfig.web_chat_widget_enabled);
    const userID = String(bootstrapUser['id']);
    const userIsAdmin = bootstrapUser['is_admin'] || bootstrapUser['is_rbac_user'];
    const userIsGuide = bootstrapUser['is_guide'];
    const [userHasPersonalMainOfficeConversation, setUserHasPersonalMainOfficeConversation] = React.useState(bootstrapUser['has_personal_main_office_conversation']);
    const [userHasPersonalAppSupportConversation, setUserHasPersonalAppSupportConversation] = React.useState(bootstrapUser['has_personal_app_support_conversation']);

    // Should probably be returned by our backend, but we'll hard-code the email pattern here for
    // now. Matches both the brand-wide `help@curatedplanet.com` and any per-licensee help-desk
    // identity (`help+<licensee.code>@curatedplanet.com`) so they're all treated as "App Support"
    // staff for chat-list label purposes.
    const userIsAppSupport = /^help(\+[^@]+)?@curatedplanet\.com$/i.test(bootstrapUser['email'] || "");
    const userIsGuest = !(userIsAdmin || userIsGuide || userIsAppSupport);

    const chatFiltersCacheKey = `chatFilters_${userID}`;
    const cachedFilters = React.useMemo(() => sharedContext.get(chatFiltersCacheKey) || {}, []);

    const chatListRowsPerPageAllowed = [5, 10, 15, 25, 50, 100];
    const [pageNum, setPageNum] = React.useState(null);
    const [perPage, setPerPage] = React.useState(() => {
        const fromLs = readChatListRowsPerPageLs(userID, chatListRowsPerPageAllowed);
        if (fromLs != null) return fromLs;

        const fromNav = normalizeChatListRowsPerPage(location?.state?.ChatListPerPage, chatListRowsPerPageAllowed);
        if (fromNav != null) return fromNav;

        const fromFilters = normalizeChatListRowsPerPage(cachedFilters.chatListRowsPerPage, chatListRowsPerPageAllowed);
        if (fromFilters != null) return fromFilters;

        return 15;
    });

    const urlParams = React.useMemo(() => {
        const sp = new URLSearchParams(location.search);
        const parsed = {};
        const boolParam = (key) => { const v = sp.get(key); return v === null ? undefined : v === '1'; };
        const msgActivityParam = (key) => {
            const v = sp.get(key);
            if (v === null) return undefined;
            if (v === '1') return MSG_ACTIVITY_HAS;
            if (v === '0') return MSG_ACTIVITY_NONE;
            return undefined;
        };
        parsed.open = boolParam('open');
        parsed.closed = boolParam('closed');
        parsed.auto_closed = boolParam('auto_closed');
        parsed.paused = boolParam('paused');
        parsed.not_paused = boolParam('not_paused');
        parsed.unread = msgActivityParam('unread');
        parsed.missing_info = msgActivityParam('missing_info');
        parsed.guest_msgs = msgActivityParam('guest_msgs');
        parsed.guide_msgs = msgActivityParam('guide_msgs');
        parsed.staff_msgs = msgActivityParam('staff_msgs');
        parsed.ai_msgs = msgActivityParam('ai_msgs');
        parsed.auto_msgs = msgActivityParam('auto_msgs');
        parsed.hide_active = boolParam('hide_active');
        parsed.needs_person = msgActivityParam('needs_person');
        parsed.hide_upcoming = boolParam('hide_upcoming');
        parsed.hide_past = boolParam('hide_past');
        parsed.order = sp.get('order') || undefined;
        parsed.itags = sp.get('itags') ? sp.get('itags').split(',').map(Number).filter(n => !isNaN(n)) : undefined;
        parsed.atags = sp.get('atags') ? sp.get('atags').split(',').map(Number).filter(n => !isNaN(n)) : undefined;
        parsed.xatags = sp.get('xatags') ? sp.get('xatags').split(',').map(Number).filter(n => !isNaN(n)) : undefined;
        parsed.ptags = sp.get('ptags') ? sp.get('ptags').split(',').map(Number).filter(n => !isNaN(n)) : undefined;
        const viewVal = sp.get('view');
        parsed.view = (viewVal === 'channels' || viewVal === 'recent_activity') ? viewVal : undefined;
        const panelsVal = sp.get('panels');
        parsed.panels = (panelsVal === '1' || panelsVal === '3') ? Number(panelsVal) : undefined;
        const hasAny = Object.values(parsed).some(v => v !== undefined);
        return hasAny ? parsed : null;
    }, []);
    const hadUrlParams = React.useRef(!!urlParams);
    // A URL that names any filter defines the whole filter set — see FILTER_URL_PARAM_KEYS.
    const urlHasFilterParams = React.useMemo(() => urlDefinesFilters(urlParams), [urlParams]);
    // Every *filter* initializes from this: the cache normally, nothing when the URL is
    // authoritative. `cachedFilters` itself stays in use for the display-only prefs
    // (chatView / show3PanelChat / rows-per-page), which a filter link doesn't reset.
    const cachedFiltersForInit = urlHasFilterParams ? {} : cachedFilters;

    const [unreadFilter, setUnreadFilter] = React.useState(() =>
        initMessageActivityTriState(
            urlParams?.unread,
            cachedFiltersForInit.unreadFilter ?? cachedFiltersForInit.onlyShowUnread ??
                (urlHasFilterParams ? undefined : location?.state?.ChatListOnlyShowUnread),
        )
    );
    const [missingInfoFilter, setMissingInfoFilter] = React.useState(() =>
        initMessageActivityTriState(
            urlParams?.missing_info,
            cachedFiltersForInit.missingInfoFilter ?? cachedFiltersForInit.onlyShowMissingInfo,
        )
    );
    const showAllOrUnreadOnly = React.useMemo(() => ({
        onlyShowUnread: unreadFilter === MSG_ACTIVITY_HAS,
        resource: unreadFilter === MSG_ACTIVITY_HAS ? "chat/unread_only" : "chat",
    }), [unreadFilter]);
    const onlyShowMissingInfo = missingInfoFilter === MSG_ACTIVITY_HAS;

    /*const [filterByAssignedAdmin, setFilterByAssignedAdmin] = React.useState({
        allAdmins: [{first_name: "Unassigned", id: -1}],
        excluded_assigned_to_admins: [],
        resource: "tour_operator/admins",
    });*/
    const [filteredInternalTags, setFilteredInternalTags] = React.useState(cachedFiltersForInit.filteredInternalTags ?? [])
    const [filteredAccessTags, setFilteredAccessTags] = React.useState(cachedFiltersForInit.filteredAccessTags ?? [])
    const [excludedAccessTags, setExcludedAccessTags] = React.useState(cachedFiltersForInit.excludedAccessTags ?? [])
    const [filteredParticipantTags, setFilteredParticipantTags] = React.useState(cachedFiltersForInit.filteredParticipantTags ?? [])
    // CP-8136: staff/guide-group channels are hidden server-side by default;
    // this opts back in. hasStaffChatGroups (from the list response) gates
    // whether the menu toggle renders at all.
    const [showStaffGroups, setShowStaffGroups] = React.useState(cachedFiltersForInit.showStaffGroups ?? false)
    // "Needs a person": conversations the chatbot handed off that nobody has replied to yet
    // (tri-state like the other Show Only filters: any / only those / all but those).
    const [needsPersonFilter, setNeedsPersonFilter] = React.useState(() =>
        initMessageActivityTriState(urlParams?.needs_person, cachedFiltersForInit.needsPersonFilter)
    );
    const [hasStaffChatGroups, setHasStaffChatGroups] = React.useState(false)
    const [show3PanelChat, setShow3PanelChat] = React.useState(urlParams?.panels !== undefined ? urlParams.panels === 3 : cachedFilters.show3PanelChat ?? true)
    const [chatView, setChatView] = React.useState(urlParams?.view ?? cachedFilters.chatView ?? 'channels') // 'channels' or 'recent_activity'
    const isRecentActivityView = chatView === 'recent_activity';
    const [selectedChannelID, setSelectedChannelID] = React.useState(routeParamsChannelID.current);
    let selectedChannelIDRef = React.useRef(routeParamsChannelID.current);
    let selectedChannelRowRef = React.useRef(null);

    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('lg');
    const { enabled: chatThemesEnabled } = useChatTheme();
    const show1pageScreen = isSmallScreen || show3PanelChat === false;

    const { settings, hasPerm, isFranchise, currentLicensee, tourOperator } = React.useContext(ContextStore);

    // Parent TO id for the franchise. Under franchise, every user's `tour_operator` is the parent
    // ("users belong to the parent" invariant in the franchise plan), so ContextStore.tourOperator
    // is the parent for any franchise user. Used to (a) decide when the licensee suffix is needed
    // on category rows and (b) sort the parent's App Support row to the top of the SUPPORT section.
    const parentTourOperatorId = isFranchise ? tourOperator?.id : null;

    // When the dashboard is viewing "All licensees" of a franchise and the list contains multiple
    // Main Office / App Support entries (one per licensee the user is staffed on), append the
    // licensee name so they're distinguishable. Suppressed when only one of that category is in
    // the visible list -- otherwise the suffix is just visual noise.
    const showLicenseeSuffixCandidate = isFranchise && !currentLicensee;
    const channelDisplayName = (row, gridApi) => {
        const base = (row.channel_category_type === "main_office" && userIsGuest === true)
            ? "New Bookings"
            : row.default_channel_name_without_date_or_tags;
        if (!showLicenseeSuffixCandidate) return base;
        if (!["main_office", "app_support", "web_widget"].includes(row.channel_category_type)) return base;
        if (!row.tour_operator?.name) return base;
        if (gridApi) {
            let count = 0;
            const models = gridApi.getRowModels();
            for (const r of models.values()) {
                if (r.channel_category_type === row.channel_category_type) {
                    count += 1;
                    if (count > 1) break;
                }
            }
            if (count <= 1) return base;
        }
        return `${base} — ${row.tour_operator.name}`;
    };

    // Order SUPPORT-section rows:
    //   0 — Main Office (always first within SUPPORT)
    //   1 — Website inquiries (widget visitors + contact-form submissions; shares Main Office's persisted sort slot on the
    //       backend, so the deterministic under-Main-Office placement lives here)
    //   2 — App Support for the parent (or any App Support when standalone)
    //   3 — App Support for any licensee (order among siblings preserved from the backend response)
    //   4 — everything else (group-tour channels etc. — relative order preserved so ACTIVE TOURS
    //       grouping stays intact; Array.prototype.sort is stable)
    const sortChatListRows = React.useCallback((rows) => {
        if (!Array.isArray(rows)) return rows;
        const supportRank = (r) => {
            if (r.channel_category_type === "main_office") return 0;
            if (r.channel_category_type === "web_widget") return 1;
            if (r.channel_category_type === "app_support") {
                return (isFranchise && parentTourOperatorId && r.tour_operator?.id !== parentTourOperatorId) ? 3 : 2;
            }
            return 4;
        };
        return rows.slice().sort((a, b) => supportRank(a) - supportRank(b));
    }, [isFranchise, parentTourOperatorId]);


    const refreshFromBackendFunc = React.useRef(null);
    const activityViewRemoveUnreadBadgeRef = React.useRef(null);
    const activityViewUpdateIssuesStateBadgeRef = React.useRef(null);
    const activityViewUpdateUnreadBadgeCountRef = React.useRef(null);
    const activityViewRefreshFromBackendFuncRef = React.useRef(null);

    const [selectedScreen, setSelectedScreen] = React.useState(routeParamsChannelID.current === null ? "ChatList" : routeParamsConversationUniqueName.current === null ? "ConversationsList" : "ChatScreen");

    const [selectedRowBadgeKey, setSelectedRowBadgeKey] = React.useState(null);
    const [openStatusFilter, setOpenStatusFilter] = React.useState(urlParams?.open ?? cachedFiltersForInit.openStatusFilter ?? true);
    const [closedStatusFilter, setClosedStatusFilter] = React.useState(urlParams?.closed ?? cachedFiltersForInit.closedStatusFilter ?? true);
    const [autoClosedStatusFilter, setAutoClosedStatusFilter] = React.useState(urlParams?.auto_closed ?? cachedFiltersForInit.autoClosedStatusFilter ?? true);

    const [notPausedNotificationsStatusFilter, setNotPausedNotificationsStatusFilter] = React.useState(urlParams?.not_paused ?? cachedFiltersForInit.notPausedNotificationsStatusFilter ?? true);
    const [pausedNotificationsStatusFilter, setPausedNotificationsStatusFilter] = React.useState(urlParams?.paused ?? cachedFiltersForInit.pausedNotificationsStatusFilter ?? true);
    
    const [hasGuestMessagesFilter, setHasGuestMessagesFilter] = React.useState(() =>
        initMessageActivityTriState(urlParams?.guest_msgs, cachedFiltersForInit.hasGuestMessagesFilter)
    );
    const [hasGuideMessagesFilter, setHasGuideMessagesFilter] = React.useState(() =>
        initMessageActivityTriState(urlParams?.guide_msgs, cachedFiltersForInit.hasGuideMessagesFilter)
    );
    const [hasStaffMessagesFilter, setHasStaffMessagesFilter] = React.useState(() =>
        initMessageActivityTriState(urlParams?.staff_msgs, cachedFiltersForInit.hasStaffMessagesFilter)
    );
    const [hasAIAutoRepliesFilter, setHasAIAutoRepliesFilter] = React.useState(() =>
        initMessageActivityTriState(urlParams?.ai_msgs, cachedFiltersForInit.hasAIAutoRepliesFilter)
    );
    const [hasAutomatedNotificationsFilter, setHasAutomatedNotificationsFilter] = React.useState(() =>
        initMessageActivityTriState(urlParams?.auto_msgs, cachedFiltersForInit.hasAutomatedNotificationsFilter)
    );

    const [excludedDateCategories, setExcludedDateCategories] = React.useState({
        excludeActiveTours: urlParams?.hide_active ?? cachedFiltersForInit.excludedDateCategories?.excludeActiveTours ?? false,
        excludeUpcomingTours: urlParams?.hide_upcoming ?? cachedFiltersForInit.excludedDateCategories?.excludeUpcomingTours ?? false,
        excludePastTours: urlParams?.hide_past ?? cachedFiltersForInit.excludedDateCategories?.excludePastTours ?? false,
    })

    const [excludedDateCategoriesLabels, setExcludedDateCategoriesLabels] = React.useState({
        excluded_date_categories: [excludedDateCategories.excludeActiveTours ? 'active_tours' : '',
        excludedDateCategories.excludeUpcomingTours ? 'upcoming_tours' : '',
        excludedDateCategories.excludePastTours ? 'past_tours' : '']
    })

    const [orderChannelsBy, setOrderChannelsBy] = React.useState(urlParams?.order ?? cachedFiltersForInit.orderChannelsBy ?? "departure")
    const lastEffectiveOrderRef = React.useRef(null)
    
    // Compute effective order for initial state - ensures we never start with "departure" in Activity view
    const getEffectiveOrder = (currentOrder, isActivityView) => {
        if (isActivityView && currentOrder === "departure") {
            return "newest_msgs";
        } else if (!isActivityView && currentOrder === "newest_msgs") {
            return "departure";
        }
        return currentOrder;
    }

    const [extraGetParams, setExtraGetParams] = React.useState(() => {
        // Use effective order in initial state to prevent backend call with wrong value
        const effectiveOrder = getEffectiveOrder(orderChannelsBy, isRecentActivityView);
        lastEffectiveOrderRef.current = effectiveOrder;
        return {
        ...excludedDateCategoriesLabels,
        filtered_internal_tags: filteredInternalTags.map(a => a.id),
        filtered_access_tags: filteredAccessTags.map(a => a.id),
        excluded_access_tags: excludedAccessTags.map(a => a.id),
        filtered_participant_tags: filteredParticipantTags.map(a => a.id),
        show_staff_groups: showStaffGroups,
        needs_person: needsPersonFilter === MSG_ACTIVITY_HAS,
        not_needs_person: needsPersonFilter === MSG_ACTIVITY_NONE,
        excluded_filtered_statuses: [...openStatusFilter === false ? [1] : [], ...closedStatusFilter === false ? [0] : [], ...autoClosedStatusFilter === false ? [2] : []],
        excluded_filtered_notification_statuses: [...pausedNotificationsStatusFilter === false ? [true] : [], ...notPausedNotificationsStatusFilter === false ? [false] : []],
        only_show_missing_info: missingInfoFilter === MSG_ACTIVITY_HAS,
        hide_missing_info: missingInfoFilter === MSG_ACTIVITY_NONE,
        hide_unread: unreadFilter === MSG_ACTIVITY_NONE,
            order_channels_by: effectiveOrder,
        has_guest_messages: hasGuestMessagesFilter === MSG_ACTIVITY_HAS,
        lacks_guest_messages: hasGuestMessagesFilter === MSG_ACTIVITY_NONE,
        has_guide_messages: hasGuideMessagesFilter === MSG_ACTIVITY_HAS,
        lacks_guide_messages: hasGuideMessagesFilter === MSG_ACTIVITY_NONE,
        has_office_staff_messages: hasStaffMessagesFilter === MSG_ACTIVITY_HAS,
        lacks_office_staff_messages: hasStaffMessagesFilter === MSG_ACTIVITY_NONE,
        has_system_auto_reply_messages: hasAIAutoRepliesFilter === MSG_ACTIVITY_HAS,
        lacks_system_auto_reply_messages: hasAIAutoRepliesFilter === MSG_ACTIVITY_NONE,
        has_automated_notification_messages: hasAutomatedNotificationsFilter === MSG_ACTIVITY_HAS,
        lacks_automated_notification_messages: hasAutomatedNotificationsFilter === MSG_ACTIVITY_NONE,
        };
    })

    const onRowClick = async (params, ev, api) => {
        //console.debug('ChatList: row click', params, ev, api, params.row);
        //window.history.replaceState(null, "Chat", "/chat");
        //window.history.replaceState(null, "Chat", "/chat/" + params.row.id + "/conversations");
        setRelatedLoading(true)
        if(searchText?.length && params.row.has_inactive_tour_conversations){
            await unarchive_channel(params.row.id);
            params.row.has_inactive_tour_conversations = false;
        }
        history.push("/chat/" + params.row.id + "/conversations", {
            from: "ChatList",
            ChatListSearchText: searchText,
            ChatListOnlyShowUnread: showAllOrUnreadOnly.onlyShowUnread,
            ChatListPageNum: pageNum,
            ChatListPerPage: perPage
        });
        
        routeParamsChannelID.current = null;
        selectedChannelRowRef.current = params.row;
        setSelectedChannelID(params.row.id);
        selectedChannelIDRef.current = params.row.id;
        setSelectedScreen("ConversationsList");
        setRelatedLoading(false);
    }

    const onSelectionModelChange = (ids, selection) => {
    }

    /*const fetchTourOpAdmins = async () => {
        const res = await dataAPI(GET_LIST, filterByAssignedAdmin.resource, {});
        setFilterByAssignedAdmin({...filterByAssignedAdmin, allAdmins: [{first_name: "Unassigned", id: -1}].concat(res)})
        console.log("ChatList.fetchTourOpAdmins: res: ", res)
    }*/

    // This is mainly for portrait mode. Since sharedContext gets update in menu.js and 
    // the menu sidebar is hidden (sandwich menu) in portrait, the sharedContext isn't updated.
    // (it's probably overkill to rely on the menu.js code to update the sharedContext unread count anyway)
    const doubleCheckUnreadCount = async () => {
        if (sharedContext.get('unreadMsgCount') === 0) {
            setTimeout(() => (async () => {
                const res = await dataAPI(GET_ONE, 'chat/unread_msg_count', {})
                sharedContext.set('unreadMsgCount', res.data.unread_message_count);
                setHasUnreadMessages(res.data.unread_message_count > 0)
            })(), 3000);
        }
    }

    const setDateAndSearchText = (date_text) => {
        var res = searchText.match(/\d{4}([\/.-])\d{2}\1\d{2}/g);
        var newSearchText = searchText;

        if(res){
            // if there is already a date, replace it
            newSearchText = newSearchText.replace(res, date_text)
        }
        else{
            // otherwise, prepend it to search
            newSearchText = date_text + " " + searchText
        }
        setSearchText(newSearchText)
        setDateSearchText(date_text)
    }

    React.useEffect(() => {
        let client = null;
        (async () => {
            console.log("ChatList: Getting chat client...")
            client = await getTwilioClient()
            console.log("ChatList: Client loaded!")

            client?.on("messageAdded", messageAddedHandler);
            client?.on('participantUpdated', participantUpdatedHandler);

            doubleCheckUnreadCount();
        })();

        showAllOrUnread(showAllOrUnreadOnly.onlyShowUnread);

        return () => {
            console.debug('ChatList: Cleaning up messageAdded event listener');
            client?.removeListener("messageAdded", messageAddedHandler);
            client?.removeListener('participantUpdated', participantUpdatedHandler);
        }
    }, []);

    /*React.useEffect(()=> {
        fetchTourOpAdmins();
    }, [])*/

    React.useEffect(() => {
        const unlisten = history.listen((location) => {            
            const url_parts = location.pathname?.split("/");
            console.log(`ChatList: Page changed to: ${location.pathname} -- ${routeParamsChannelID.current} -- ${selectedChannelID} ${selectedChannelIDRef.current} -- ${url_parts} `)
            if (url_parts && url_parts.length >= 3){
                if (parseInt(selectedChannelIDRef.current) !== parseInt(url_parts[2])){
                    routeParamsChannelID.current = url_parts[2];
                    selectedChannelRowRef.current = null;
                    setSelectedChannelID(routeParamsChannelID.current);
                    selectedChannelIDRef.current = routeParamsChannelID.current;
                    console.log(`ChatList: Page changed; Selected channel ID changed to: ${routeParamsChannelID.current}`);
                }
                if (url_parts.length >= 5){
                    setSelectedScreen("ChatScreen")
                }
                else{
                    setSelectedScreen("ConversationsList")
                }

                /*if (location?.state?.from !== "ConversationsListSearch") {
                    //clear ConversationsList search bar only
                    const elements = document.querySelectorAll(["#ConversationsList .MuiDataGrid-toolbarContainer .MuiInputBase-root .MuiButtonBase-root"])
                    elements.forEach(element => {
                        element.click()
                        setSearchText("");
                    })
                }*/
            }
            else{
                routeParamsChannelID.current = null;
                selectedChannelRowRef.current = null;
                setSelectedChannelID(null)
                routeParamsChannelID.current = null;
                setSelectedScreen("ChatList")
                
                //clear both search bars (#ConversationsList is a child of #ChatList)
                const elements = document.querySelectorAll(["#ChatList .MuiDataGrid-toolbarContainer .MuiInputBase-root .MuiButtonBase-root"])

                if (location?.state?.from === "ConversationsList") {
                    setSearchText(location?.state?.ChatListSearchText || "")
                }
                else{
                    elements.forEach(element => {
                        element.click()
                        setSearchText("");
                    })
                }
            }
        });
        
        // Return cleanup function to unsubscribe
        return () => {
            unlisten();
        };
    }, [history])

    React.useEffect(() => {
        setExcludedDateCategoriesLabels({excluded_date_categories:[excludedDateCategories.excludeActiveTours ? 'active_tours' : '',
                                                                   excludedDateCategories.excludeUpcomingTours ? 'upcoming_tours' : '',
                                                                   excludedDateCategories.excludePastTours ? 'past_tours' : '']})
    }, [excludedDateCategories])

    React.useEffect(() => {
        if(!searchText || searchText.length === 0){
            setDateSearchText("");
        }
    }, [searchText])

    // Refresh user data on mount to get latest conversation flags
    React.useEffect(() => {
        if (!userID) {
            console.log('ChatList: No user ID found, skipping user data refresh');
            return;
        }
        console.log('ChatList: Refreshing user data for user ID:', userID);
        dataAPI(GET_ONE, 'user', { id: userID }).then((res) => {
            if (res.statusCode === 200 && res.data) {
                const updatedUser = res.data;
                console.log('ChatList: Updated user data:', res);
                // Update state
                setUserHasPersonalMainOfficeConversation(updatedUser.has_personal_main_office_conversation);
                setUserHasPersonalAppSupportConversation(updatedUser.has_personal_app_support_conversation);
                
                // Update bootstrap in sharedContext and localStorage
                const existingBootstrap = sharedContext.get('bootstrap') || JSON.parse(window.localStorage.getItem('bootstrap')) || {};
                const updatedBootstrap = {
                    ...existingBootstrap,
                    user: {
                        ...existingBootstrap.user,
                        ...updatedUser,
                    }
                };
                sharedContext.set('bootstrap', updatedBootstrap);
                window.localStorage.setItem('bootstrap', JSON.stringify(updatedBootstrap));
            }
            else{
                console.error('ChatList: Error refreshing user data:', res);
            }
        }).catch((error) => {
            console.error('ChatList: Error refreshing user data:', error);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Track previous view to detect view changes
    const prevIsRecentActivityViewForLayoutRef = React.useRef(isRecentActivityView);
    const prevIsRecentActivityViewForEffectRef = React.useRef(isRecentActivityView);
    // Track previous orderChannelsBy to detect auto-switch changes
    const prevOrderChannelsByRef = React.useRef(orderChannelsBy);
    // Track if we're currently in a view transition (to prevent redundant updates)
    const isViewTransitionRef = React.useRef(false);
    
    // Combined useLayoutEffect: Auto-change orderChannelsBy AND update extraGetParams when switching views
    // This ensures both happen synchronously before render, preventing redundant backend calls
    React.useLayoutEffect(() => {
        const viewJustChanged = prevIsRecentActivityViewForLayoutRef.current !== isRecentActivityView;
        prevIsRecentActivityViewForLayoutRef.current = isRecentActivityView;
        
        if (viewJustChanged) {
            // Mark that we're in a view transition
            isViewTransitionRef.current = true;
            
            // Auto-switch orderChannelsBy
            if (isRecentActivityView) {
                // Switch to "newest_msgs" when entering Activity view
                setOrderChannelsBy("newest_msgs");
            } else {
                // Switch back to "departure" when leaving Activity view for Channels view
                setOrderChannelsBy("departure");
            }
            
            // Immediately compute and set effective order for extraGetParams
            // Use the view-specific default order directly
            const effectiveOrder = isRecentActivityView ? "newest_msgs" : "departure";
            lastEffectiveOrderRef.current = effectiveOrder;
            
            setExtraGetParams({
                ...excludedDateCategoriesLabels,
                filtered_internal_tags: filteredInternalTags.map(a => a.id),
                filtered_access_tags: filteredAccessTags.map(a => a.id),
                excluded_access_tags: excludedAccessTags.map(a => a.id),
                filtered_participant_tags: filteredParticipantTags.map(a => a.id),
                show_staff_groups: showStaffGroups,
                needs_person: needsPersonFilter === MSG_ACTIVITY_HAS,
                not_needs_person: needsPersonFilter === MSG_ACTIVITY_NONE,
                excluded_filtered_statuses: [...openStatusFilter === false ? [1] : [], ...closedStatusFilter === false ? [0] : [], ...autoClosedStatusFilter === false ? [2] : []],
                excluded_filtered_notification_statuses: [...pausedNotificationsStatusFilter === false ? [true] : [], ...notPausedNotificationsStatusFilter === false ? [false] : []],
                only_show_missing_info: missingInfoFilter === MSG_ACTIVITY_HAS,
                hide_missing_info: missingInfoFilter === MSG_ACTIVITY_NONE,
                hide_unread: unreadFilter === MSG_ACTIVITY_NONE,
                order_channels_by: effectiveOrder,
                has_guest_messages: hasGuestMessagesFilter === MSG_ACTIVITY_HAS,
                lacks_guest_messages: hasGuestMessagesFilter === MSG_ACTIVITY_NONE,
                has_guide_messages: hasGuideMessagesFilter === MSG_ACTIVITY_HAS,
                lacks_guide_messages: hasGuideMessagesFilter === MSG_ACTIVITY_NONE,
                has_office_staff_messages: hasStaffMessagesFilter === MSG_ACTIVITY_HAS,
                lacks_office_staff_messages: hasStaffMessagesFilter === MSG_ACTIVITY_NONE,
                has_system_auto_reply_messages: hasAIAutoRepliesFilter === MSG_ACTIVITY_HAS,
                lacks_system_auto_reply_messages: hasAIAutoRepliesFilter === MSG_ACTIVITY_NONE,
                has_automated_notification_messages: hasAutomatedNotificationsFilter === MSG_ACTIVITY_HAS,
                lacks_automated_notification_messages: hasAutomatedNotificationsFilter === MSG_ACTIVITY_NONE,
                ...activeDuringParams(activeDuring),
            })
            
            // Clear the flag after the current render cycle completes
            setTimeout(() => {
                isViewTransitionRef.current = false;
            }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecentActivityView]) // Only run when view changes
    
    // Regular effect for other dependency changes (not view changes)
    // Note: isRecentActivityView is NOT in dependencies - view changes are handled by useLayoutEffect above
    React.useEffect(() => {
        // Skip if we're in a view transition - all updates are handled by useLayoutEffect
        if (isViewTransitionRef.current) {
            // Update refs to prevent stale state
            prevOrderChannelsByRef.current = orderChannelsBy;
            prevIsRecentActivityViewForEffectRef.current = isRecentActivityView;
            return;
        }
        
        // Check if view just changed - the useLayoutEffect above handles view changes
        const viewJustChanged = prevIsRecentActivityViewForEffectRef.current !== isRecentActivityView;
        prevIsRecentActivityViewForEffectRef.current = isRecentActivityView;
        
        if (viewJustChanged) {
            // View change is handled by useLayoutEffect, skip this update
            prevOrderChannelsByRef.current = orderChannelsBy;
            return;
        }
        
        // Check if orderChannelsBy changed and matches auto-switch pattern
        const orderChanged = prevOrderChannelsByRef.current !== orderChannelsBy;
        const isAutoSwitchPattern = orderChanged && (
            (isRecentActivityView && orderChannelsBy === "newest_msgs" && prevOrderChannelsByRef.current === "departure") ||
            (!isRecentActivityView && orderChannelsBy === "departure" && prevOrderChannelsByRef.current === "newest_msgs")
        );
        
        // Update prevOrderChannelsByRef
        prevOrderChannelsByRef.current = orderChannelsBy;
        
        if (isAutoSwitchPattern) {
            // This is an auto-switch due to view change, skip it (useLayoutEffect already handled it)
            return;
        }
        
        // Compute the effective order: use view-specific default if orderChannelsBy matches the old default
        // This prevents redundant backend calls when switching views
        const effectiveOrder = getEffectiveOrder(orderChannelsBy, isRecentActivityView);
        
        // Update lastEffectiveOrderRef for tracking
        lastEffectiveOrderRef.current = effectiveOrder;
        
        setExtraGetParams({
            ...excludedDateCategoriesLabels,
            filtered_internal_tags: filteredInternalTags.map(a => a.id),
            filtered_access_tags: filteredAccessTags.map(a => a.id),
            excluded_access_tags: excludedAccessTags.map(a => a.id),
            filtered_participant_tags: filteredParticipantTags.map(a => a.id),
            show_staff_groups: showStaffGroups,
            needs_person: needsPersonFilter === MSG_ACTIVITY_HAS,
            not_needs_person: needsPersonFilter === MSG_ACTIVITY_NONE,
            excluded_filtered_statuses: [...openStatusFilter === false ? [1] : [], ...closedStatusFilter === false ? [0] : [], ...autoClosedStatusFilter === false ? [2] : []],
            excluded_filtered_notification_statuses: [...pausedNotificationsStatusFilter === false ? [true] : [], ...notPausedNotificationsStatusFilter === false ? [false] : []],
            only_show_missing_info: missingInfoFilter === MSG_ACTIVITY_HAS,
            hide_missing_info: missingInfoFilter === MSG_ACTIVITY_NONE,
            hide_unread: unreadFilter === MSG_ACTIVITY_NONE,
            order_channels_by: effectiveOrder,
            ...(isRecentActivityView ? { is_recent_activity_view: true } : {}),
            has_guest_messages: hasGuestMessagesFilter === MSG_ACTIVITY_HAS,
            lacks_guest_messages: hasGuestMessagesFilter === MSG_ACTIVITY_NONE,
            has_guide_messages: hasGuideMessagesFilter === MSG_ACTIVITY_HAS,
            lacks_guide_messages: hasGuideMessagesFilter === MSG_ACTIVITY_NONE,
            has_office_staff_messages: hasStaffMessagesFilter === MSG_ACTIVITY_HAS,
            lacks_office_staff_messages: hasStaffMessagesFilter === MSG_ACTIVITY_NONE,
            has_system_auto_reply_messages: hasAIAutoRepliesFilter === MSG_ACTIVITY_HAS,
            lacks_system_auto_reply_messages: hasAIAutoRepliesFilter === MSG_ACTIVITY_NONE,
            has_automated_notification_messages: hasAutomatedNotificationsFilter === MSG_ACTIVITY_HAS,
            lacks_automated_notification_messages: hasAutomatedNotificationsFilter === MSG_ACTIVITY_NONE,
            ...activeDuringParams(activeDuring),
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDuring, excludedDateCategoriesLabels, filteredInternalTags, filteredAccessTags, excludedAccessTags, filteredParticipantTags, showStaffGroups, needsPersonFilter, openStatusFilter, closedStatusFilter, autoClosedStatusFilter, pausedNotificationsStatusFilter, notPausedNotificationsStatusFilter, missingInfoFilter, unreadFilter, orderChannelsBy, hasGuestMessagesFilter, hasGuideMessagesFilter, hasStaffMessagesFilter, hasAIAutoRepliesFilter, hasAutomatedNotificationsFilter, chatView])

    React.useEffect(() => {
        writeChatListRowsPerPageLs(userID, perPage);
        // A filter URL replaces the cached filter set outright, so the pre-CP-7386 legacy keys
        // are dropped rather than carried forward — otherwise a filter the URL just cleared
        // could still be resurrected through the `?? cachedFilters.onlyShow*` fallbacks.
        const carriedOver = { ...(sharedContext.get(chatFiltersCacheKey) || {}) };
        if (urlHasFilterParams) {
            delete carriedOver.onlyShowUnread;
            delete carriedOver.onlyShowMissingInfo;
        }
        sharedContext.set(chatFiltersCacheKey, {
            ...carriedOver,
            openStatusFilter,
            closedStatusFilter,
            autoClosedStatusFilter,
            pausedNotificationsStatusFilter,
            notPausedNotificationsStatusFilter,
            unreadFilter,
            missingInfoFilter,
            filteredInternalTags,
            filteredAccessTags,
            excludedAccessTags,
            filteredParticipantTags,
            showStaffGroups,
            needsPersonFilter,
            excludedDateCategories,
            orderChannelsBy,
            hasGuestMessagesFilter,
            hasGuideMessagesFilter,
            hasStaffMessagesFilter,
            hasAIAutoRepliesFilter,
            hasAutomatedNotificationsFilter,
            chatView,
            show3PanelChat,
            chatListRowsPerPage: perPage,
        });
    }, [chatFiltersCacheKey, userID, urlHasFilterParams, openStatusFilter, closedStatusFilter, autoClosedStatusFilter, pausedNotificationsStatusFilter, notPausedNotificationsStatusFilter, unreadFilter, missingInfoFilter, filteredInternalTags, filteredAccessTags, excludedAccessTags, filteredParticipantTags, showStaffGroups, needsPersonFilter, excludedDateCategories, orderChannelsBy, hasGuestMessagesFilter, hasGuideMessagesFilter, hasStaffMessagesFilter, hasAIAutoRepliesFilter, hasAutomatedNotificationsFilter, chatView, show3PanelChat, perPage]);

    React.useEffect(() => {
        if (!hadUrlParams.current) return;
        let cancelled = false;
        const resolveUrlTags = async () => {
            const itagIds = urlParams?.itags;
            const atagIds = urlParams?.atags;
            const fetches = [];
            if (itagIds?.length) {
                fetches.push(
                    dataAPI(GET_LIST, 'tour_tag', {
                        filter: { filters: JSON.stringify([{ field: 'id', op: 'isAnyOf', value: itagIds }]) },
                        pagination: { page: 1, perPage: itagIds.length },
                    }).then(res => { if (!cancelled && res.data?.length) setFilteredInternalTags(res.data); })
                      .catch(e => console.error('Failed to resolve internal tag IDs from URL', e))
                );
            }
            if (atagIds?.length) {
                fetches.push(
                    dataAPI(GET_LIST, 'access_tag', {
                        filter: { filters: JSON.stringify([{ field: 'id', op: 'isAnyOf', value: atagIds }]) },
                        pagination: { page: 1, perPage: atagIds.length },
                    }).then(res => { if (!cancelled && res.data?.length) setFilteredAccessTags(res.data); })
                      .catch(e => console.error('Failed to resolve access tag IDs from URL', e))
                );
            }
            const xatagIds = urlParams?.xatags;
            if (xatagIds?.length) {
                fetches.push(
                    dataAPI(GET_LIST, 'access_tag', {
                        filter: { filters: JSON.stringify([{ field: 'id', op: 'isAnyOf', value: xatagIds }]) },
                        pagination: { page: 1, perPage: xatagIds.length },
                    }).then(res => { if (!cancelled && res.data?.length) setExcludedAccessTags(res.data); })
                      .catch(e => console.error('Failed to resolve excluded access tag IDs from URL', e))
                );
            }
            const ptagIds = urlParams?.ptags;
            if (ptagIds?.length) {
                fetches.push(
                    dataAPI(GET_LIST, 'tour_tag', {
                        filter: { filters: JSON.stringify([{ field: 'id', op: 'isAnyOf', value: ptagIds }]) },
                        pagination: { page: 1, perPage: ptagIds.length },
                    }).then(res => { if (!cancelled && res.data?.length) setFilteredParticipantTags(res.data); })
                      .catch(e => console.error('Failed to resolve participant tag IDs from URL', e))
                );
            }
            await Promise.all(fetches);
        };
        resolveUrlTags();
        history.replace({ pathname: location.pathname, state: location.state });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const messageAddedHandler = async (data) => {
        const bootstrapUser = JSON.parse(window.localStorage.getItem('bootstrap'))?.user;
        // Signed out (bootstrap wiped, e.g. by a logout in another tab): this tab's chat client is stale.
        if (!bootstrapUser) return;
        console.debug("ChatList: messageAddedHandler: message added!");
        setListRefreshKey((listRefreshKey) => listRefreshKey + 1);
        // add a small delay to give our backend time to process the new message
        //setTimeout(() => (async () => { await refreshFromBackendFunc.current() })(), 500);

        //console.debug("ChatList: messageAddedHandler: message added (second refresh)!");
        // in case it takes extra long, add an additional refresh for insurance
        setTimeout(() => (async () => { await refreshFromBackendFunc.current();})(), 3500);

        const tw_identity = bootstrapUser['twilio_identity'];
        const msg_author = data.state.author;
        if(msg_author !== tw_identity){
            doubleCheckUnreadCount();
        }
    }

    const participantUpdatedHandler = async (data) => {
        const bootstrapUser = JSON.parse(window.localStorage.getItem('bootstrap'))?.user;
        // Signed out (bootstrap wiped, e.g. by a logout in another tab): this tab's chat client is stale.
        if (!bootstrapUser) return;
        const tw_identity = bootstrapUser['twilio_identity'];
        const updated_participant_identity = await data.participant.identity;
        console.debug("ChatList: participantUpdatedHandler: participant updated!", data, tw_identity, updated_participant_identity);
        if (updated_participant_identity === tw_identity) {
            setListRefreshKey((listRefreshKey) => listRefreshKey + 1);
            // add a small delay to give our backend time to process the new message
            //setTimeout(() => (async () => { await refreshFromBackendFunc.current() })(), 500);
            // in case it takes extra long, add an additional refresh for insurance
            setTimeout(() => (async () => { await refreshFromBackendFunc.current() })(), 3500);
        }
        // keep here in case of shared read receipts
        doubleCheckUnreadCount();
    };

    const showAllOrUnread = (checked) => {
        // The top-right Switch is 2-state; map true -> Has, false -> Any. The "no unread" state
        // (None) is only reachable from the menu's tri-state control.
        const next = checked ? MSG_ACTIVITY_HAS : MSG_ACTIVITY_ANY;
        if (unreadFilter !== next) resetChatList();
        setUnreadFilter(next);
    }
    
    const resetChatList = () => {
        setPageNum(1)
        history.push("/chat")
        routeParamsChannelID.current = null;
        selectedChannelRowRef.current = null;
        setSelectedChannelID(null)
        routeParamsChannelID.current = null;
    }

    const show1or3PanelChat = (checked) => {

        if (checked === true) {
            setShow3PanelChat(true)
        }
        else {
            //setSelectedScreen("ChatList")
            setShow3PanelChat(false)
        }
    }

    const getFilterShareURL = () => {
        const params = new URLSearchParams();
        if (!openStatusFilter) params.set('open', '0');
        if (!closedStatusFilter) params.set('closed', '0');
        if (!autoClosedStatusFilter) params.set('auto_closed', '0');
        if (!pausedNotificationsStatusFilter) params.set('paused', '0');
        if (!notPausedNotificationsStatusFilter) params.set('not_paused', '0');
        if (unreadFilter === MSG_ACTIVITY_HAS) params.set('unread', '1');
        else if (unreadFilter === MSG_ACTIVITY_NONE) params.set('unread', '0');
        if (missingInfoFilter === MSG_ACTIVITY_HAS) params.set('missing_info', '1');
        else if (missingInfoFilter === MSG_ACTIVITY_NONE) params.set('missing_info', '0');
        if (hasGuestMessagesFilter === MSG_ACTIVITY_HAS) params.set('guest_msgs', '1');
        else if (hasGuestMessagesFilter === MSG_ACTIVITY_NONE) params.set('guest_msgs', '0');
        if (hasGuideMessagesFilter === MSG_ACTIVITY_HAS) params.set('guide_msgs', '1');
        else if (hasGuideMessagesFilter === MSG_ACTIVITY_NONE) params.set('guide_msgs', '0');
        if (hasStaffMessagesFilter === MSG_ACTIVITY_HAS) params.set('staff_msgs', '1');
        else if (hasStaffMessagesFilter === MSG_ACTIVITY_NONE) params.set('staff_msgs', '0');
        if (hasAIAutoRepliesFilter === MSG_ACTIVITY_HAS) params.set('ai_msgs', '1');
        else if (hasAIAutoRepliesFilter === MSG_ACTIVITY_NONE) params.set('ai_msgs', '0');
        if (hasAutomatedNotificationsFilter === MSG_ACTIVITY_HAS) params.set('auto_msgs', '1');
        else if (hasAutomatedNotificationsFilter === MSG_ACTIVITY_NONE) params.set('auto_msgs', '0');
        if (excludedDateCategories.excludeActiveTours) params.set('hide_active', '1');
        if (needsPersonFilter === MSG_ACTIVITY_HAS) params.set('needs_person', '1');
        else if (needsPersonFilter === MSG_ACTIVITY_NONE) params.set('needs_person', '0');
        if (excludedDateCategories.excludeUpcomingTours) params.set('hide_upcoming', '1');
        if (excludedDateCategories.excludePastTours) params.set('hide_past', '1');
        const defaultOrder = isRecentActivityView ? "newest_msgs" : "departure";
        if (orderChannelsBy !== defaultOrder) params.set('order', orderChannelsBy);
        if (filteredInternalTags?.length) params.set('itags', filteredInternalTags.map(t => t.id).join(','));
        if (filteredAccessTags?.length) params.set('atags', filteredAccessTags.map(t => t.id).join(','));
        if (excludedAccessTags?.length) params.set('xatags', excludedAccessTags.map(t => t.id).join(','));
        if (filteredParticipantTags?.length) params.set('ptags', filteredParticipantTags.map(t => t.id).join(','));
        if (chatView !== 'channels') params.set('view', chatView);
        if (!show3PanelChat) params.set('panels', '1');
        const qs = params.toString();
        return `${window.location.origin}${location.pathname}${qs ? '?' + qs : ''}`;
    };

    const filtersHaveChanged = () => {
        // In Activity view, "newest_msgs" is the default, so don't consider it a changed filter
        const defaultOrderChannelsBy = isRecentActivityView ? "newest_msgs" : "departure";
        return (closedStatusFilter === false ||
            openStatusFilter === false ||
            autoClosedStatusFilter === false ||
            pausedNotificationsStatusFilter === false ||
            notPausedNotificationsStatusFilter === false ||
            missingInfoFilter !== MSG_ACTIVITY_ANY ||
            unreadFilter !== MSG_ACTIVITY_ANY ||
            excludedDateCategories['excludeActiveTours'] === true ||
            excludedDateCategories['excludeUpcomingTours'] === true ||
            excludedDateCategories['excludePastTours'] === true ||
            filteredInternalTags?.length > 0 ||
            filteredAccessTags?.length > 0 ||
            excludedAccessTags?.length > 0 ||
            filteredParticipantTags?.length > 0 ||
            showStaffGroups === true ||
            needsPersonFilter !== MSG_ACTIVITY_ANY ||
            orderChannelsBy !== defaultOrderChannelsBy ||
            hasGuestMessagesFilter !== MSG_ACTIVITY_ANY ||
            hasGuideMessagesFilter !== MSG_ACTIVITY_ANY ||
            hasStaffMessagesFilter !== MSG_ACTIVITY_ANY ||
            hasAIAutoRepliesFilter !== MSG_ACTIVITY_ANY ||
            hasAutomatedNotificationsFilter !== MSG_ACTIVITY_ANY ||
            activeDuring !== null
        )
    }
    const activeFilterCount = () => {
        const defaultOrderChannelsBy = isRecentActivityView ? "newest_msgs" : "departure";
        return [
            closedStatusFilter === false,
            openStatusFilter === false,
            autoClosedStatusFilter === false,
            pausedNotificationsStatusFilter === false,
            notPausedNotificationsStatusFilter === false,
            missingInfoFilter !== MSG_ACTIVITY_ANY,
            unreadFilter !== MSG_ACTIVITY_ANY,
            excludedDateCategories['excludeActiveTours'] === true,
            excludedDateCategories['excludeUpcomingTours'] === true,
            excludedDateCategories['excludePastTours'] === true,
            filteredInternalTags?.length > 0,
            filteredAccessTags?.length > 0,
            excludedAccessTags?.length > 0,
            filteredParticipantTags?.length > 0,
            showStaffGroups === true,
            needsPersonFilter !== MSG_ACTIVITY_ANY,
            orderChannelsBy !== defaultOrderChannelsBy,
            hasGuestMessagesFilter !== MSG_ACTIVITY_ANY,
            hasGuideMessagesFilter !== MSG_ACTIVITY_ANY,
            hasStaffMessagesFilter !== MSG_ACTIVITY_ANY,
            hasAIAutoRepliesFilter !== MSG_ACTIVITY_ANY,
            hasAutomatedNotificationsFilter !== MSG_ACTIVITY_ANY,
            activeDuring !== null,
        ].filter(Boolean).length;
    };

    const clearFilters = () => {
        if (filtersHaveChanged()) {
            resetChatList()
            setFilteredInternalTags([])
            setFilteredAccessTags([])
            setExcludedAccessTags([])
            setFilteredParticipantTags([])
            setShowStaffGroups(false)
            setNeedsPersonFilter(MSG_ACTIVITY_ANY)
            setExcludedDateCategories({
                excludeActiveTours: false,
                excludeUpcomingTours: false,
                excludePastTours: false,
            })
            setClosedStatusFilter(true)
            setOpenStatusFilter(true)
            setAutoClosedStatusFilter(true)
            setPausedNotificationsStatusFilter(true)
            setNotPausedNotificationsStatusFilter(true)
            setUnreadFilter(MSG_ACTIVITY_ANY)
            setMissingInfoFilter(MSG_ACTIVITY_ANY)
            // Set default based on current view
            setOrderChannelsBy(isRecentActivityView ? "newest_msgs" : "departure")
            setHasGuestMessagesFilter(MSG_ACTIVITY_ANY)
            setHasGuideMessagesFilter(MSG_ACTIVITY_ANY)
            setHasStaffMessagesFilter(MSG_ACTIVITY_ANY)
            setHasAIAutoRepliesFilter(MSG_ACTIVITY_ANY)
            setHasAutomatedNotificationsFilter(MSG_ACTIVITY_ANY)
            setActiveDuring(null)
        }
    }

    // A new Active-during range is a new result set: back to page one, as the menu's filters do.
    const applyActiveDuring = (value) => {
        resetChatList()
        setActiveDuring(value)
    }

    const ConfirmationDialogText = {
        "mark_all_as_read": { title: "Please confirm", body: "Are you sure you wish to mark all conversations as “Read”?", okButtonText: "Confirm", cancelButtonText: "Cancel" },
    }

    const markAllAsRead = async () => {
        // Sent through dataAPI so it carries the session token: the backend marks the signed-in
        // user's chats. user_id only keeps backends from before that change working (they need it);
        // the fixed backend ignores it.
        const data = { user_id: JSON.parse(window.localStorage.getItem('bootstrap'))?.user?.id };
        // Mirror the chat-list scope: when the dashboard franchisee selector is set, only mark
        // chats for that licensee as read (not every chat across the brand).
        const licenseeId = sharedContext.get('selected_licensee_id');
        if (licenseeId) {
            data.franchise_to = licenseeId;
        }

        try {
            const res = await dataAPI(CREATE, 'chat/mark_all_conversations_as_read', { data });
            if (res.statusCode >= 400) {
                console.error("ChatList: chat/mark_all_conversations_as_read: Error setting all conversations as read!", res);
                return;
            }
            sharedContext.set('unreadMsgCount', 0);
            setTimeout(() => (async () => { await refreshFromBackendFunc.current() })(), 300);
            setTimeout(() => (async () => { await refreshFromBackendFunc.current() })(), 600);
        } catch (error) {
            console.error("ChatList: chat/mark_all_conversations_as_read: Error setting all conversations as read!", error);
        }
    }

    const handleMarkRead = (ev) => {
        setConfirmationDialogAction("mark_all_as_read");
    }

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    }
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case "mark_all_as_read": markAllAsRead(); break;
            default: break;
        }
        confirmationDialogCancel();
    }


    const updateSelectedRowBadgeCount = (deltaVal) => {
        // Nothing selected in this list (e.g. the conversation was opened from a URL).
        if (!selectedChannelRowRef.current) return;
        selectedChannelRowRef.current.unread_message_count += deltaVal;
        setSelectedRowBadgeKey(Math.random())
    }

    const cols = 12

    // Compute effective extraGetParams for Activity view - ensures we never pass "departure"
    // This memo ensures ConversationsList always receives the correct order, even if extraGetParams
    // temporarily has "departure" in it during the view transition
    const effectiveExtraGetParamsForActivity = React.useMemo(() => {
        if (!isRecentActivityView) return extraGetParams;
        // Always compute effective order - in Activity view it should never be "departure"
        const effectiveOrder = getEffectiveOrder(extraGetParams.order_channels_by, true);
        
        // CRITICAL: If we're in Activity view and the order is "departure", we MUST override it
        // This prevents the redundant backend call
        if (extraGetParams.order_channels_by === "departure" || extraGetParams.order_channels_by !== effectiveOrder) {
            console.log("ChatList: Overriding order in Activity view", { 
                original: extraGetParams.order_channels_by, 
                effective: effectiveOrder 
            });
            return {
                ...extraGetParams,
                order_channels_by: effectiveOrder,
                is_recent_activity_view: true
            };
        }
        return {
            ...extraGetParams,
            is_recent_activity_view: true
        };
    }, [extraGetParams, isRecentActivityView]);

    // Compute effective extraGetParams for RecordList - adds excluded_date_categories in Activity view
    const effectiveExtraGetParamsForRecordList = React.useMemo(() => ({
        ...extraGetParams,
        ...(isRecentActivityView ? { 
            excluded_date_categories: ['active_tours', 'upcoming_tours', 'past_tours'] 
        } : {})
    }), [extraGetParams, isRecentActivityView]);

    // The Activity-view embedded grids sit below the header, so their own loading bars
    // can't render under it; they report loading here and one bar is drawn under the header instead
    const [activityConversationsLoading, setActivityConversationsLoading] = React.useState(false);
    const [activitySupportLoading, setActivitySupportLoading] = React.useState(false);

    // Calculate the number of support conversations the user has access to
    const supportConversationsCount = React.useMemo(() => {
        if (userHasPersonalAppSupportConversation && userHasPersonalMainOfficeConversation) {
            return 2;
        } else if (userHasPersonalAppSupportConversation || userHasPersonalMainOfficeConversation) {
            return 1;
        }
        return 0;
    }, [userHasPersonalAppSupportConversation, userHasPersonalMainOfficeConversation]);

    // The SUPPORT section sizes itself to its content (title + auto-height rows), so
    // measure its real rendered height to shrink the activity list below by the same
    // amount. A hardcoded per-count height here previously let the Main Office row
    // spill under the CONVERSATIONS header when both support conversations showed.
    const supportSectionRef = React.useRef(null);
    const [measuredSupportHeight, setMeasuredSupportHeight] = React.useState(null);
    React.useEffect(() => {
        const el = supportSectionRef.current;
        if (!el) {
            setMeasuredSupportHeight(null);
            return;
        }
        const observer = new ResizeObserver(() => {
            setMeasuredSupportHeight(el.offsetHeight);
        });
        observer.observe(el);
        setMeasuredSupportHeight(el.offsetHeight);
        return () => observer.disconnect();
    }, [isRecentActivityView, supportConversationsCount]);

    const getDateRangeText = (start, end, gt_tz, checkoutSpread = null) => {
        if(!start || !end || !gt_tz){
            return null;
        }
        // Hotel channels whose guests check out on different days: "OCT 8 · CHECK-OUTS
        // OCT 10–12" rather than a range ending on the last guest's day.
        const staggered = staggeredCheckoutText(start, checkoutSpread, gt_tz);
        if (staggered) {
            return staggered;
        }

        //Subtract a millisecond from the end datetime so that if it's midnight, we get the previous date day presented in the range
        return tourDateRangeText(start, new Date(end) - 1, gt_tz);
    }

    const resetFirstsInList = () => {
        //console.debug("ChatList: resetting firsts in list")
        let firstsInListTmp = {
            "null": { "id": null, "label": "SUPPORT" },
            // CP-8136: guide groups sort between the Support block and the tour
            // sections, and are matched on their own flag rather than on a date
            // status (the backend returns null for them, which would otherwise
            // put them under SUPPORT).
            "guide_group": { "id": null, "label": "GUIDE GROUPS" },
            "active": { "id": null, "label": "ACTIVE TOURS" },
            "upcoming": { "id": null, "label": "UPCOMING TOURS" },
            "past": { "id": null, "label": "PAST TOURS" }
        }
        return firstsInListTmp;
    }

    let firstsInList = React.useRef(resetFirstsInList());
    let prevRow = React.useRef(null);
    let prevRows = React.useRef({});

    if (!hasPerm('chat', 'list')) {
        return <AccessDeniedView />
    }

    // Note that the style here updates the height of all 3 panels on mobile
    return <div id="ChatList"
        className={(!show1pageScreen || selectedScreen === "ChatList") ? classes.root : null}
        style={{ height: window.innerWidth < 500 ? mobileChatPaneHeight() : null }}>
        <PageMeta title="Chat Channels" />
        {/*<div style={{flexBasis: "100%", width:"100%"}}>NEW HEADER?</div>*/}
        <Grid container direction="row" spacing={2} flex={1} style={{minWidth: show1pageScreen ? null : !isRecentActivityView ? 360 : 500, display: (show1pageScreen && selectedScreen !== "ChatList") ? "none" : null}}>
            <Grid item xs={cols} style={{ height: '100%', ...(window.innerWidth < 500 ? { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } : {}) }}>
                <div style={{ height: 4 }} aria-hidden />
                <AppBar position="relative" elevation={0} style={styles.header(show1pageScreen)}>
                    {/*
                    <FormControlLabel style={{ position: "absolute", right: -6, top: 4 }} control={<Switch size="small" color="primary" onChange={e => showAllOrUnread(e.target.checked)} checked={showAllOrUnreadOnly.onlyShowUnread} />} label={<Typography style={{fontSize: ".75rem"}}>{"Unread"}</Typography>} />
                    */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Button
                                onClick={() => setChatView('channels')}
                                startIcon={chatView === 'channels' ? 
                                    <ChatBubbleIcon sx={{ fontSize: '1.2rem', color: cv('text', '#000'), marginRight: '-6px' }} /> :
                                    <ChatBubbleOutlineIcon sx={{ fontSize: '1.2rem', color: cv('text-secondary', '#666'), marginRight: '-6px' }} />
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: chatView === 'channels' ? cv('text', '#000') : cv('text-secondary', '#666'),
                                    backgroundColor: chatView === 'channels' ? cv('accent-tint', '#E3F2FD') : 'transparent',
                                    padding: '4px 8px 4px 8px',
                                    minWidth: 'auto',
                                    gap: '4px',
                                    borderRadius: chatView === 'channels' ? '8px' : '0px',
                                    '&:hover': {
                                        backgroundColor: chatView === 'channels' ? cv('accent-tint', '#E3F2FD') : cv('surface-muted', 'rgba(0, 0, 0, 0.04)'),
                                    }
                                }}
                            >
                                All channels
                            </Button>
                            <Button
                                onClick={() => setChatView('recent_activity')}
                                startIcon={chatView === 'recent_activity' ?
                                    <NotificationsIcon sx={{ fontSize: '1.2rem', color: cv('text', '#000'), marginRight: '-8px' }} /> :
                                    <NotificationsNoneIcon sx={{ fontSize: '1.2rem', color: cv('text-secondary', '#666'), marginRight: '-8px' }} />
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: chatView === 'recent_activity' ? cv('text', '#000') : cv('text-secondary', '#666'),
                                    backgroundColor: chatView === 'recent_activity' ? cv('accent-tint', '#E3F2FD') : 'transparent',
                                    padding: '4px 8px 4px 8px',
                                    minWidth: 'auto',
                                    gap: '4px',
                                    borderRadius: chatView === 'recent_activity' ? '8px' : '0px',
                                    '&:hover': {
                                        backgroundColor: chatView === 'recent_activity' ? cv('accent-tint', '#E3F2FD') : cv('surface-muted', 'rgba(0, 0, 0, 0.04)'),
                                    }
                                }}
                            >
                                Activity
                            </Button>
                        </div>
                        {(!isSmallScreen || chatThemesEnabled) &&
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: 'auto' }}>
                            <ChatThemeMenuButton />
                            {!isSmallScreen &&
                                <>
                                    <Typography style={{ fontSize: "0.75rem", color: cv('text-secondary', '#666') }}>
                                        {show3PanelChat ? "3 panel" : "1 panel"}
                                    </Typography>
                                    <Switch 
                                        size="small" 
                                        color="primary" 
                                        onChange={e => show1or3PanelChat(e.target.checked)} 
                                        checked={show3PanelChat}
                                        sx={{
                                            '& .MuiSwitch-thumb': {
                                                backgroundColor: show3PanelChat ? cv('accent-fill', '#007AFF') : '#fff',
                                            },
                                            '& .MuiSwitch-track': {
                                                backgroundColor: show3PanelChat ? cv('accent-fill', '#007AFF') : '#ccc',
                                            }
                                        }}
                                    />
                                </>
                            }
                        </div>
                        }
                    </div>
                    { filtersHaveChanged() && (
                        <Chip
                            label={`${activeFilterCount()} filter${activeFilterCount() !== 1 ? 's' : ''} active`}
                            onDelete={clearFilters}
                            size="small"
                            sx={{
                                position: 'absolute', right: 0, top: 42, zIndex: 11,
                                backgroundColor: cv('accent-tint', '#E3F2FD'),
                                color: cv('accent', '#007AFF'),
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '22px',
                                '& .MuiChip-deleteIcon': { color: cv('accent', '#007AFF'), fontSize: '15px' },
                                '&:hover': { backgroundColor: cv('accent-tint-strong', '#BBDEFB') },
                            }}
                        />
                    )}
                    {activeDuring && (
                        <Chip
                            label={activeDuringLabel(activeDuring)}
                            onDelete={() => applyActiveDuring(null)}
                            size="small"
                            sx={{
                                position: 'absolute', right: filtersHaveChanged() ? 118 : 0, top: 42, zIndex: 11,
                                backgroundColor: cv('accent-tint', '#E3F2FD'),
                                color: cv('accent', '#007AFF'),
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '22px',
                                '& .MuiChip-deleteIcon': { color: cv('accent', '#007AFF'), fontSize: '15px' },
                                '&:hover': { backgroundColor: cv('accent-tint-strong', '#BBDEFB') },
                            }}
                        />
                    )}
                    <Tooltip title={'Filter by date'} arrow placement="top"><span variant="secondary_icon" style={{ zIndex: 10, width: "2.5rem", height: "2.5rem", position: 'absolute', top: '65px', right: '4.8rem' }}>
                        <DateFilterPopover setDateSearchFilter={setDateAndSearchText} activeDuring={activeDuring} setActiveDuring={applyActiveDuring} />
                    </span></Tooltip>
                    <Tooltip title={'Mark all conversations as Read'} arrow placement="top"><IconButton variant="secondary_icon" style={{ zIndex: 10, width: "2.5rem", height: "2.5rem", cursor: hasUnreadMessages ? 'pointer' : 'default', position: 'absolute', top: '65px', right: '2.65rem' }} onClick={(e) => hasUnreadMessages ? handleMarkRead(e) : e.preventDefault()} >
                        <MarkReadIcon fill={hasUnreadMessages ? "#007AFF" : "rgba(0, 0, 0, 0.26)"} style={{ fontSize: '2.5em' }} variant="contained" size="small" />
                    </IconButton></Tooltip>
                    <ChatListMenu
                        selectedScreen={selectedScreen}
                        excludedDateCategories={excludedDateCategories}
                        setExcludedDateCategories={setExcludedDateCategories}
                        orderChannelsBy={orderChannelsBy}
                        setOrderChannelsBy={setOrderChannelsBy}
                        isRecentActivityView={isRecentActivityView}
                        //excludedAdmins={filterByAssignedAdmin.excluded_assigned_to_admins}
                        //filterByAssignedAdmin={filterByAssignedAdmin}
                        //setFilterByAssignedAdmin={setFilterByAssignedAdmin}
                        //admins={filterByAssignedAdmin.allAdmins}
                        resetChatList={resetChatList}
                        filteredInternalTags={filteredInternalTags}
                        setFilteredInternalTags={setFilteredInternalTags}
                        filteredAccessTags={filteredAccessTags}
                        setFilteredAccessTags={setFilteredAccessTags}
                        excludedAccessTags={excludedAccessTags}
                        setExcludedAccessTags={setExcludedAccessTags}
                        filteredParticipantTags={filteredParticipantTags}
                        setFilteredParticipantTags={setFilteredParticipantTags}
                        showStaffGroups={showStaffGroups}
                        setShowStaffGroups={setShowStaffGroups}
                        needsPersonFilter={needsPersonFilter}
                        setNeedsPersonFilter={setNeedsPersonFilter}
                        hasWebWidget={hasWebWidget}
                        hasStaffChatGroups={hasStaffChatGroups}
                        openStatusFilter={openStatusFilter}
                        setOpenStatusFilter={setOpenStatusFilter}
                        closedStatusFilter={closedStatusFilter}
                        setClosedStatusFilter={setClosedStatusFilter}
                        autoClosedStatusFilter={autoClosedStatusFilter}
                        setAutoClosedStatusFilter={setAutoClosedStatusFilter}
                        notPausedNotificationsStatusFilter={notPausedNotificationsStatusFilter}
                        setNotPausedNotificationsStatusFilter={setNotPausedNotificationsStatusFilter}
                        pausedNotificationsStatusFilter={pausedNotificationsStatusFilter}
                        setPausedNotificationsStatusFilter={setPausedNotificationsStatusFilter}
                        unreadFilter={unreadFilter}
                        setUnreadFilter={setUnreadFilter}
                        missingInfoFilter={missingInfoFilter}
                        setMissingInfoFilter={setMissingInfoFilter}
                        hasGuestMessagesFilter={hasGuestMessagesFilter}
                        setHasGuestMessagesFilter={setHasGuestMessagesFilter}
                        hasGuideMessagesFilter={hasGuideMessagesFilter}
                        setHasGuideMessagesFilter={setHasGuideMessagesFilter}
                        hasStaffMessagesFilter={hasStaffMessagesFilter}
                        setHasStaffMessagesFilter={setHasStaffMessagesFilter}
                        hasAIAutoRepliesFilter={hasAIAutoRepliesFilter}
                        setHasAIAutoRepliesFilter={setHasAIAutoRepliesFilter}
                        hasAutomatedNotificationsFilter={hasAutomatedNotificationsFilter}
                        setHasAutomatedNotificationsFilter={setHasAutomatedNotificationsFilter}
                        clearFilters={clearFilters}
                        filtersHaveChanged={filtersHaveChanged}
                        getFilterShareURL={getFilterShareURL}
                    />

                    {confirmationDialogAction ?
                        <ConfirmationDialog
                            classes={{ paper: classes.paper }}
                            keepMounted
                            onCancel={confirmationDialogCancel}
                            onConfirm={confirmationDialogConfirm}
                            title={ConfirmationDialogText[confirmationDialogAction].title}
                            okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                            cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                            open={!!confirmationDialogAction}
                        >
                            {ConfirmationDialogText[confirmationDialogAction].body}
                        </ConfirmationDialog>
                        : null}
                </AppBar>
                {/* All-channels mobile: flex child so the DataGrid footer stays in
                    the pane. Activity mobile: do NOT grow — RecordList collapses
                    to the 49px search toolbar and the conversations list below
                    should take the leftover space (a flex:1 here manufactured a
                    large empty band above CONVERSATIONS). Desktop: contents so
                    the 86svh height:100% chain stays intact. */}
                <div style={window.innerWidth < 500
                    ? (isRecentActivityView
                        ? { flexShrink: 0 }
                        : { flex: 1, minHeight: 0, overflow: 'hidden' })
                    : { display: 'contents' }}>
                <RecordList
                    refreshFromBackendFunc={refreshFromBackendFunc}
                    listRefreshKey={listRefreshKey}
                    headerHeight={0}
                    className={classes.recordList}
                    resource={showAllOrUnreadOnly.resource}
                    transformRows={sortChatListRows}
                    extra_get_params={effectiveExtraGetParamsForRecordList}
                    setHasStaffChatGroups={setHasStaffChatGroups}
                    isRecentActivityView={isRecentActivityView}
                    disableSelectionOnClick={false}
                    onSelectionModelChange={onSelectionModelChange}
                    onSearchTextChange={setSearchText}
                    key={dateSearchText}
                    searchText={searchText}
                    initialSearchText={searchText}
                    deps={lastUpdate}
                    onRowClick={onRowClick}
                    relatedLoading={relatedLoading}
                    query={query}
                    show1pageScreen={show1pageScreen}
                    rowHeight={80}
                    //listItemRenderer={listItemRenderer}
                    checkboxSelection={false}
                    resetFirstsInList={resetFirstsInList}
                    firstsInList={firstsInList}
                    pageNum={pageNum}
                    perPage={perPage}
                    setPageNum={setPageNum}
                    setPerPage={setPerPage}
                    setTotal={setChatListRowTotal}
                    getSelectedRow={selectedChannelRowRef.current?.id}
                    searchPlaceholder={"Search by tour, name, booking, tag"}
                    parent={"ChatList"}
                    hideFooter={isRecentActivityView ? true : (false && searchText?.length)}
                    hideBottomBorder={isRecentActivityView}
                    rowsPerPageOptions={(searchText?.length || isRecentActivityView) && [5]}
                    columns={[
                        {
                            field: 'default_channel_name_without_date_or_tags', headerName: 'Channel Name', sortable: false, flex: 5.00,
                            renderCell: p => {
                                return (
                                    <div style={{width: "100%"}}>
                                        <div>
                                            {(() => {
                                                // handle redirects (for example, from clicking a notification)
                                                if (routeParamsChannelID.current !== null && selectedChannelRowRef.current?.id !== routeParamsChannelID.current) {
                                                    if (p.row.id === parseInt(routeParamsChannelID.current)) {
                                                        setSelectedChannelID(p.row.id);
                                                        routeParamsChannelID.current = p.row.id;
                                                        selectedChannelRowRef.current = p.row;
                                                        routeParamsChannelID.current = null;
                                                    }
                                                }
                                                // make sure the ref gets updated on re-renders
                                                if (selectedChannelRowRef.current?.id === p.row.id && selectedChannelRowRef.current !== p.row){
                                                    selectedChannelRowRef.current = p.row;
                                                }

                                                const sectionKey = p.row.is_staff_chat_group
                                                    ? "guide_group"
                                                    : String(p.row.group_tour_date_status);
                                                for (let key in firstsInList.current) {
                                                    if (key === sectionKey) {
                                                        if (firstsInList.current[key]['id'] === null || firstsInList.current[key]['id'] === p.id) {
                                                            firstsInList.current[key]['id'] = p.id;
                                                            prevRow.current = null;
                                                            prevRows.current[p.row.id] = null;
                                                            return (
                                                                <Typography
                                                                    style={{
                                                                        ...styles.dateStatusLabel,
                                                                        ...(p.api.getRowIndex(p.id) === 0
                                                                            ? { borderTop: 'none' }
                                                                            : {}),
                                                                    }}
                                                                >
                                                                    {firstsInList.current[key]['label']}
                                                                </Typography>
                                                            )
                                                        }
                                                    }
                                                }
                                            })()}
                                        </div>
                                        {(() => {
                                            if ((sharedContext.get('unreadMsgCount') > 0) !== hasUnreadMessages) {
                                                setHasUnreadMessages(sharedContext.get('unreadMsgCount') > 0)
                                            }
                                        })()}
                                        {(() => {
                                            let prevRowTmp = prevRows.current[p.row.id] || prevRow.current
                                            if (p.row.group_tour_id == null || prevRow.current == null || (prevRowTmp.group_tour_id !== p.row.group_tour_id || prevRowTmp.tour_start_datetime !== p.row.tour_start_datetime) || prevRowTmp.num_channels_with_same_departure < 2) {
                                                if (p.row.id !== prevRow.current?.id) {
                                                    prevRows.current[p.row.id] = prevRow.current
                                                    prevRow.current = p.row;
                                                }
                                                return (
                                                    <span style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", alignItems: "center", paddingTop: 4, paddingBottom: p.row.group_tour_id && p.row.num_channels_with_same_departure > 1 ? 2 : 4, backgroundColor: p.row.group_tour_id && p.row.num_channels_with_same_departure > 1 ? cv('surface', 'white') : null, marginLeft: -10, marginRight: -10, paddingLeft: 10, paddingRight: 10 }}>
                                                        <span style={styles.groupedTourBadge(p.row.num_channels_with_same_departure > 1)}>{p.row.num_channels_with_same_departure}</span>
                                                        {(() => {
                                                            if (p.row.channel_category_type === "main_office") {
                                                                return (<MainOfficeIcon style={styles.coverImg} />)
                                                            }
                                                            else if (p.row.channel_category_type === "app_support") {
                                                                return (<AppSupportIcon style={styles.coverImg} />)
                                                            }
                                                            else if (p.row.channel_category_type === "web_widget") {
                                                                return (<WebChatIcon style={styles.coverImg} />)
                                                            }
                                                            else {
                                                                if (p.row.id !== prevRow.current?.id) {
                                                                    prevRows.current[p.row.id] = prevRow.current
                                                                    prevRow.current = p.row;
                                                                }

                                                                const imageHeight = 43;
                                                                const imageWidth = 43;
                                                                const cover_img = p.row.cover_image;
                                                                const imgUrl = (cover_img && cover_img.p && cover_img.s) ? images.url(settings.images.base, cover_img, `cover_${imageWidth}x${imageHeight}`) : null;
                                                                if (imgUrl !== null && imgUrl !== "null") {
                                                                    return (<img src={`${imgUrl}`} alt="" style={styles.coverImg} />)
                                                                }
                                                                else {
                                                                    console.warn(`ChatList: Group tour cover image for ${p.row.default_channel_name_without_date_or_tags} is empty! Using placeholder...`)
                                                                    return (<GroupTourCoverPlaceholder style={styles.coverImg} />)
                                                                }
                                                            }
                                                        })()}
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            {(() => {
                                                                    return (
                                                                        <>
                                                                            <OverflowTooltip title={channelDisplayName(p.row, p.api)}><Typography style={styles.conversationName(p.row.group_tour_id == null && (p.row.unread_message_count) > 0)}>
                                                                                {channelDisplayName(p.row, p.api)}
                                                                            </Typography></OverflowTooltip>
                                                                            <Typography style={styles.previewText((!(p.row.num_channels_with_same_departure > 1) && p.row.unread_message_count) > 0, show1pageScreen, ["main_office", "app_support", "web_widget"].includes(p.row.channel_category_type) || p.row.is_staff_chat_group === true)}>
                                                                                <span style={{display: !(["main_office", "app_support", "web_widget"].includes(p.row.channel_category_type) || p.row.is_staff_chat_group === true) ? "inline-block" : null, marginRight: 6}}>{DOMPurify.sanitize(
                                                                                    (() => {
                                                                                        if (p.row.main_office_or_app_support_preview_text_for_user) {
                                                                                            return (p.row.main_office_or_app_support_preview_text_for_user);
                                                                                        }
                                                                                        else {
                                                                                            if (p.row.channel_category_type === "main_office") {
                                                                                                if (userIsAdmin === true && hasPerm('chat', 'read')) {
                                                                                                    return ("Chat with guests and guides.");
                                                                                                }
                                                                                                else if (userIsGuide === true || hasPerm('chat', 'list')) {
                                                                                                    return ("Chat with our main office here.");
                                                                                                }
                                                                                                else {
                                                                                                    return ("Chat with our booking team.");
                                                                                                }
                                                                                            }
                                                                                            else if (p.row.channel_category_type === "app_support") {
                                                                                                if (userIsAppSupport === true) {
                                                                                                    return ("Answer questions about the app.");
                                                                                                }
                                                                                                else {
                                                                                                    return ("Chat with our app support team.");
                                                                                                }
                                                                                            }
                                                                                            else if (p.row.channel_category_type === "web_widget") {
                                                                                                return ("Chats and contact-form inquiries from your website.");
                                                                                            }
                                                                                            else if (p.row.is_staff_chat_group) {
                                                                                                // CP-8136: a guide group's dates are an artifact of its tour
                                                                                                // modelling and mean nothing (it never expires) -- showing them
                                                                                                // reads as a bug ("FEB 12 – FEB 11"). The backend serves the
                                                                                                // latest message in the preview field checked above (same as
                                                                                                // the SUPPORT rows); this branch is only the empty-channel
                                                                                                // fallback, so the row keeps its two-line height.
                                                                                                return "No messages yet.";
                                                                                            }
                                                                                            else {
                                                                                                return getDateRangeText(p.row.tour_start_datetime, p.row.tour_end_datetime, p.row.group_tour_timezone, p.row.checkout_spread);
                                                                                            }
                                                                                        }
                                                                                    })()).replace(/<\/?[^>]+(>|$)/g, "")
                                                                                    // DOMPurify turns a raw "<" in preview text into &lt;, which would
                                                                                    // show literally here since this renders as a React child. &amp;
                                                                                    // last so "&lt;" typed as text survives.
                                                                                    .replace(/&lt;/g, '<')
                                                                                    .replace(/&gt;/g, '>')
                                                                                    .replace(/&amp;/g, '&')}
                                                                                </span>
                                                                                {(() => {
                                                                                    let prevRowTmp = prevRows.current[p.row.id]
                                                                                    if (p.row.tour_tags) {
                                                                                        const tag_spans = p.row.tour_tags.flatMap(tag => p.row.num_channels_with_same_departure > 1 && (tag.category !== 'departure' || !(prevRowTmp?.group_tour_id !== p.row.group_tour_id || prevRowTmp?.tour_start_datetime !== p.row.tour_start_datetime)) ? [] :
                                                                                            <Tag key={tag.id} title={formatTourTagsTooltip(p.row.tour_tags)} style={{ fontSize: ".88rem", padding: "2px 4px 2px 4px", marginRight: 4, color: cv('text', 'black'), background: selectedChannelRowRef.current?.id === p.id ? tag.category === 'departure' ? p.row.num_channels_with_same_departure > 1 ? chatColors.tourTagDeparture : cv('surface', 'white') : cv('tag-tan', '#fcfaf5') : tag.category === 'departure' ? chatColors.tourTagDeparture : chatColors.tourTag, borderRadius: 3, lineHeight: 1.15, display:"inline-block" }}>{tag.data?.friendly_name || tag.name}</Tag>
                                                                                        )
                                                                                        return tag_spans
                                                                                    }
                                                                                })()}

                                                                            </Typography>
                                                                        </>
                                                                    )
                                                                
                                                            })()}

                                                        </div>
                                                        {(!p.row.group_tour_id || !(p.row.num_channels_with_same_departure > 1)) && (
                                                            <div
                                                                key={p.row.id === selectedChannelRowRef.current?.id ? selectedRowBadgeKey : null}
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                    flexWrap: 'nowrap',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'flex-end',
                                                                    marginLeft: 'auto',
                                                                    flexShrink: 0,
                                                                    marginRight: 2,
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {p.row.has_inactive_tour_conversations && searchText?.length > 0 ? (
                                                                    <ArchiveOutlinedIcon style={{ color: cv('text-muted', 'rgba(0, 0, 0, .5)'), height: '17px', width: '17px', minWidth: '16px', flexShrink: 0 }} />
                                                                ) : null}
                                                                {p.row.is_muted_for_user ? (
                                                                    <NotificationBellOffGray style={{ height: '17px', width: '17px', minWidth: '16px', flexShrink: 0 }} />
                                                                ) : null}
                                                                {p.row.all_notifications_paused ? (
                                                                    <PauseAutoCommunicationsGray style={{ height: '17px', width: '17px', minWidth: '16px', flexShrink: 0 }} />
                                                                ) : null}
                                                                {p.row.unread_message_count > 0 ? (
                                                                    <span style={styles.unreadBadge(p.row.unread_message_count, false)}>{p.row.unread_message_count}</span>
                                                                ) : null}
                                                                {p.row.issues_state === 0 && <Tooltip title="Issues closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19, height: 19 }}><IssuesClosedIcon style={{ width: 19, height: 19, display: 'block' }}/></span></Tooltip>}
                                                                {(p.row.issues_state === 2 && !p.row.latest_message_is_system_auto_reply) && <Tooltip title="Issues auto-closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 21, height: 19 }}><IssuesAutoClosedIcon style={{ width: 21.7, height: 19.8, display: 'block', transform: 'translateY(1.5px)' }}/></span></Tooltip>}
                                                                {p.row.latest_message_is_handoff && <Tooltip title="Handed off — the chatbot stopped and is waiting for your team" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19, height: 19 }}><ChatbotHandoffIcon style={{ width: 19, height: 19, display: 'block' }}/></span></Tooltip>}
                                                                {(p.row.issues_state !== 2 && p.row.latest_message_is_system_auto_reply && !p.row.latest_message_is_handoff) && <Tooltip title="AI auto-replied" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19 }}><SystemAutoReplyIcon style={{ width: 19, height: 17, display: 'block', transform: 'translateY(0.5px)' }}/></span></Tooltip>}
                                                                {(p.row.issues_state === 2 && p.row.latest_message_is_system_auto_reply && !p.row.latest_message_is_handoff) && <Tooltip title="AI auto-replied and auto-closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19 }}><SystemAutoReplyAutoClosedIcon style={{ width: 19, height: 17, display: 'block', transform: 'translateY(0.5px)' }}/></span></Tooltip>}
                                                            </div>
                                                        )}

                                                    </span>)
                                            }
                                            else {
                                                if (p.row.id !== prevRow.current?.id) {
                                                    prevRows.current[p.row.id] = prevRow.current
                                                    prevRow.current = p.row;
                                                }
                                            }
                                        })()}
                                        {p.row.group_tour_id && p.row.num_channels_with_same_departure > 1 &&
                                            <span style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", alignItems: "center", marginTop: "4px", marginBottom: "4px" }}>
                                                <div style={{ marginLeft: "54px", marginTop: "2px", marginBottom: "2px", flex: 1, minWidth: 0 }}>
                                                    {(() => {
                                                        //let prevRowTmp = prevRows.current[p.row.id]
                                                        if (p.row.tour_tags && p.row.tour_tags.filter((t) => t.category !== 'departure').length > 0) {
                                                            let tag_spans = p.row.tour_tags.flatMap(tag => tag.category === 'departure' ? [] :
                                                                <Tag key={tag.id} title={formatTourTagsTooltip(p.row.tour_tags)} style={{ fontSize: ".88rem", padding: "3px 4px 3px 4px", marginLeft: 4, color: cv('text', 'black'), fontWeight: p.row.unread_message_count > 0 ? 500 : null, background: selectedChannelRowRef.current?.id === p.id ? tag.category === 'departure' ? cv('surface', 'white') : cv('tag-tan', '#fcfaf5') : tag.category === 'departure' ? chatColors.tourTagDeparture : chatColors.tourTag, borderRadius: 3, lineHeight: 1.15, display:"inline-block" }}>{tag.data?.friendly_name || tag.name}</Tag>
                                                            )
                                                            // unnasigned tag (unused)
                                                            //if(prevRowTmp?.tour_tags.length > p.row.tour_tags.length){
                                                            //    tag_spans.push(<span style={{ fontSize: ".88rem", padding: 4, marginLeft: 4, color: "black", background: selectedChannelRowRef.current?.id === p.id ? "#f2ebec" : "#f2d8da", borderRadius: 3 }}>UNASSIGNED</span>)
                                                            //}
                                                            return tag_spans
                                                        }
                                                        else {
                                                            let departure_tag_name = p.row.tour_tags.filter((t) => t.category === 'departure')[0]?.name
                                                            departure_tag_name = departure_tag_name ? departure_tag_name + ", " : ""
                                                            return <Tag title={departure_tag_name + "Main"} style={{ fontSize: ".88rem", padding: "2px 4px 2px 4px", marginLeft: 4, color: cv('text', 'black'), fontWeight: p.row.unread_message_count > 0 ? 500 : null, background: selectedChannelRowRef.current?.id === p.id ? cv('surface', 'white') : chatColors.tourTag, borderRadius: 3, lineHeight: 1.15, display:"inline-block" }}>Main</Tag>
                                                        }
                                                    })()}
                                                </div>
                                                <div
                                                    key={p.row.id === selectedChannelRowRef.current?.id ? selectedRowBadgeKey : null}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        flexWrap: 'nowrap',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        marginLeft: 'auto',
                                                        flexShrink: 0,
                                                        marginRight: 2,
                                                        gap: 4,
                                                    }}
                                                >
                                                    {p.row.is_muted_for_user ? (
                                                        <NotificationBellOffGray style={{ height: '17px', width: '17px', minWidth: '16px', flexShrink: 0 }} />
                                                    ) : null}
                                                    {p.row.all_notifications_paused ? (
                                                        <PauseAutoCommunicationsGray style={{ height: '17px', width: '17px', minWidth: '16px', flexShrink: 0 }} />
                                                    ) : null}
                                                    {p.row.unread_message_count > 0 ? (
                                                        <span style={styles.unreadBadge(p.row.unread_message_count, true)}>{p.row.unread_message_count}</span>
                                                    ) : null}
                                                    {p.row.issues_state === 0 && <Tooltip title="Issues closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19, height: 19 }}><IssuesClosedIcon style={{ width: 19, height: 19, display: 'block' }}/></span></Tooltip>}
                                                    {(p.row.issues_state === 2 && !p.row.latest_message_is_system_auto_reply) && <Tooltip title="Issues auto-closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 21, height: 19 }}><IssuesAutoClosedIcon style={{ width: 21.7, height: 19.8, display: 'block', transform: 'translateY(1.5px)' }}/></span></Tooltip>}
                                                    {p.row.latest_message_is_handoff && <Tooltip title="Handed off — the chatbot stopped and is waiting for your team" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19, height: 19 }}><ChatbotHandoffIcon style={{ width: 19, height: 19, display: 'block' }}/></span></Tooltip>}
                                                    {(p.row.issues_state !== 2 && p.row.latest_message_is_system_auto_reply && !p.row.latest_message_is_handoff) && <Tooltip title="AI auto-replied" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19 }}><SystemAutoReplyIcon style={{ width: 19, height: 17, display: 'block', transform: 'translateY(0.5px)' }}/></span></Tooltip>}
                                                    {(p.row.issues_state === 2 && p.row.latest_message_is_system_auto_reply && !p.row.latest_message_is_handoff) && <Tooltip title="AI auto-replied and auto-closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19 }}><SystemAutoReplyAutoClosedIcon style={{ width: 19, height: 17, display: 'block', transform: 'translateY(0.5px)' }}/></span></Tooltip>}
                                                </div>

                                            </span>}
                                        {p.row.group_tour_id && p.row.num_channels_with_same_departure > 1 &&
                                            <hr style={{ height: "1px", marginLeft: "54px", marginTop: 0, marginBottom: 0, border: `1px solid ${cv('border', '#F5F5F5')}`, borderBottom: "0px solid" }} />
                                        }
                                    </div>
                                )
                            }
                        },
                    ]}
                />
                </div>
                {isRecentActivityView && (activityConversationsLoading || activitySupportLoading) &&
                    <div style={{ position: 'relative', height: 0, zIndex: 5 }} aria-hidden>
                        <LinearProgress style={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
                    </div>}
                {isRecentActivityView && (userHasPersonalMainOfficeConversation || userHasPersonalAppSupportConversation) &&
                <div ref={supportSectionRef}>
                <ConversationsList
                    isEmbeddedInChatList={true}
                    show1pageScreen={show1pageScreen}
                    show3PanelChat={show3PanelChat}
                    selectedScreen={"ConversationsList"}
                    setSelectedScreen={setSelectedScreen}
                    selectedChannelCategory={null}
                    refreshChatListFromBackendFunc={refreshFromBackendFunc.current}
                    isRecentActivityView={true}
                    isRecentActivitySupportView={true}
                    extra_get_params={{excluded_date_categories: ['active_tours', 'upcoming_tours', 'past_tours'], only_include_signed_in_user_conversations: true}}
                    title_override_text={"SUPPORT"}
                    support_conversation_count={supportConversationsCount}
                    hideFooter={true}
                    hideBottomBorder={true}
                    hideLoadingIndicator={true}
                    onLoadingChange={setActivitySupportLoading}
                    autoHeight={true}
                />
                </div>}
                {isRecentActivityView &&
                <div style={window.innerWidth < 500 ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : undefined}>
                <ConversationsList
                    isEmbeddedInChatList={true}
                    show1pageScreen={show1pageScreen}
                    show3PanelChat={show3PanelChat}
                    selectedScreen={"ConversationsList"}
                    setSelectedScreen={setSelectedScreen}
                    selectedChannelName={null}
                    selectedChannelDateRange={null}
                    selectedChannelTags={null}
                    selectedChannelThumbnail={null}
                    selectedChannelGroupTourId={null}
                    selectedChannelCategory={null}
                    shouldShowUnreadOnly={showAllOrUnreadOnly.onlyShowUnread}
                    refreshChatListFromBackendFunc={refreshFromBackendFunc.current}
                    conversationsListSearchText={searchText}
                    extra_get_params={{...effectiveExtraGetParamsForActivity, exclude_signed_in_user_conversations: true}}
                    chatListRowTotal={chatListRowTotal}
                    onlyShowMissingInfo={onlyShowMissingInfo}
                    orderChannelsBy={orderChannelsBy}
                    isRecentActivityView={true}
                    hideLoadingIndicator={true}
                    onLoadingChange={setActivityConversationsLoading}
                    onRemoveUnreadBadgeRef={activityViewRemoveUnreadBadgeRef}
                    onUpdateIssuesStateBadgeRef={activityViewUpdateIssuesStateBadgeRef}
                    onUpdateUnreadBadgeCountRef={activityViewUpdateUnreadBadgeCountRef}
                    onRefreshFromBackendFuncRef={activityViewRefreshFromBackendFuncRef}
                    support_conversation_count={supportConversationsCount}
                    heightOverride={window.innerWidth < 500 ? '100%' : undefined}
                    heightReductionOverride={(() => {
                        // Estimate (~34px title + ~54px per row) covers the frames
                        // before the ResizeObserver reports the section's real height.
                        const estimate = supportConversationsCount === 1 ? 88 : supportConversationsCount === 2 ? 146 : 0;
                        return `${(measuredSupportHeight ?? estimate)}px`;
                    })()}
                />
                </div>}
                {searchText != null && searchText !== "" && !isRecentActivityView &&
                <ConversationsList
                    isEmbeddedInChatList={true}
                    show1pageScreen={show1pageScreen}
                    show3PanelChat={show3PanelChat}
                    selectedScreen={"ConversationsList"}
                    setSelectedScreen={setSelectedScreen}
                    selectedChannelName={selectedChannelRowRef.current?.default_channel_name_without_date_or_tags}
                    selectedChannelDateRange={selectedChannelRowRef.current ? getDateRangeText(selectedChannelRowRef.current.tour_start_datetime, selectedChannelRowRef.current.tour_end_datetime, selectedChannelRowRef.current.group_tour_timezone, selectedChannelRowRef.current.checkout_spread) : null}
                    selectedChannelTags={selectedChannelRowRef.current?.tour_tags}
                    selectedChannelThumbnail={selectedChannelRowRef.current?.cover_image}
                    selectedChannelGroupTourId={selectedChannelRowRef.current?.group_tour_id}
                    selectedChannelCategory={selectedChannelRowRef.current?.channel_category_type}
                    selectedChannelIsStaffChatGroup={selectedChannelRowRef.current?.is_staff_chat_group}
                    shouldShowUnreadOnly={showAllOrUnreadOnly.onlyShowUnread}
                    refreshChatListFromBackendFunc={refreshFromBackendFunc.current}
                    //updateSelectedRowBadgeCount={updateSelectedRowBadgeCount}
                    conversationsListSearchText={searchText}
                    extra_get_params={extraGetParams}
                    chatListRowTotal={chatListRowTotal}
                    onlyShowMissingInfo={onlyShowMissingInfo}
                    orderChannelsBy={orderChannelsBy}
                />}
            </Grid>
        </Grid>
        {console.log("ChatScreen: show", show1pageScreen, show3PanelChat, isSmallScreen)}
        <ConversationsList
            show1pageScreen={show1pageScreen}
            show3PanelChat={show3PanelChat}
            isSmallScreen={isSmallScreen}
            selectedScreen={selectedScreen}
            setSelectedScreen={setSelectedScreen}
            selectedChannelID={isRecentActivityView ? null : selectedChannelID}
            selectedChannelName={isRecentActivityView ? null : selectedChannelRowRef.current?.default_channel_name_without_date_or_tags}
            selectedChannelDateRange={isRecentActivityView ? null : (selectedChannelRowRef.current ? getDateRangeText(selectedChannelRowRef.current.tour_start_datetime, selectedChannelRowRef.current.tour_end_datetime, selectedChannelRowRef.current.group_tour_timezone, selectedChannelRowRef.current.checkout_spread) : null)}
            selectedChannelTags={isRecentActivityView ? null : selectedChannelRowRef.current?.tour_tags}
            selectedChannelThumbnail={isRecentActivityView ? null : selectedChannelRowRef.current?.cover_image}
            selectedChannelGroupTourId={isRecentActivityView ? null : selectedChannelRowRef.current?.group_tour_id}
            selectedChannelCategory={isRecentActivityView ? null : selectedChannelRowRef.current?.channel_category_type}
            selectedChannelIsStaffChatGroup={isRecentActivityView ? null : selectedChannelRowRef.current?.is_staff_chat_group}
            shouldShowUnreadOnly={showAllOrUnreadOnly.onlyShowUnread}
            refreshChatListFromBackendFunc={refreshFromBackendFunc.current}
            extra_get_params={extraGetParams}
            onlyShowMissingInfo={onlyShowMissingInfo}
            orderChannelsBy={orderChannelsBy}
            updateSelectedRowBadgeCount={updateSelectedRowBadgeCount}
            chatView={chatView}
            setChatView={setChatView}
            isEmbeddedInChatList={false}
            activityViewRemoveUnreadBadge={isRecentActivityView ? (activityViewRemoveUnreadBadgeRef.current || null) : null}
            activityViewUpdateIssuesStateBadge={isRecentActivityView ? (activityViewUpdateIssuesStateBadgeRef.current || null) : null}
            activityViewUpdateUnreadBadgeCount={isRecentActivityView ? (activityViewUpdateUnreadBadgeCountRef.current || null) : null}
            activityViewRefreshFromBackendFunc={isRecentActivityView ? (activityViewRefreshFromBackendFuncRef || null) : null}
        />

    </div>;

};

const styles = {
    conversationName: (hasUnreadMessages) => ({
        fontWeight: hasUnreadMessages ? '600' : '500',
        fontSize: '1.05rem',
        marginLeft: '15px'
    }),
    dateStatusLabel: {
        fontSize: '.9rem',
        color: cv('text-muted', '#8A8A8F'),
        fontWeight: '600',
        paddingTop: 12,
        borderTop: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        backgroundColor: cv('surface', 'white'),
        marginLeft: -10,
        marginRight: -10,
        paddingLeft: 12,
    },
    previewText: (hasUnreadMessages, show1pageScreen, isSupportChannel) => ({
        fontWeight: hasUnreadMessages ? '500' : 'normal',
        color: hasUnreadMessages ? cv('text', 'black') : cv('text-muted', '#6c757d'),
        fontSize: '.88rem',
        minHeight: '1.35rem',
        marginLeft: '15px',
        //lineHeight: '1.8',
        maxWidth: !show1pageScreen && isSupportChannel ? "calc(28vw - 180px)": null,//maxWidth: !show1pageScreen ? "calc(100% - 35px)": null,

    }),
    header: (show1pageScreen) => ({
        color: cv('text', 'black'),
        backgroundColor: cv('surface-muted', '#F5F5F5'),
        marginTop: 0,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 12,
        //alignItems: 'center',
        height: 61,
        borderRadius: !show1pageScreen ? "10px 0px 0px" : "10px 10px 0px 0px",
        borderTop: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        borderLeft: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        borderRight: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
    }),
    coverImg: {
        height: '43px',
        width: '43px',
        minWidth: '43px',
        borderRadius: '50%',
        marginTop: '2px',
    },
    unreadBadge: (unread_message_count, is_group_tour_row) => ({
        // One digit: a 17px circle. More: a pill that grows with the number (rounded ends,
        // 3px either side) instead of squeezing into the circle.
        borderRadius: '8.5px',
        fontWeight: '700',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        display: unread_message_count > 0 ? "inline-flex" : "none",
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minWidth: '17px',
        height: '17px',
        fontSize: '13px',
        fontVariantNumeric: 'tabular-nums',
        color: 'white',
        backgroundColor: cv('accent-fill', '#007AFF'),
        lineHeight: '1',
        boxSizing: 'border-box',
        // Nudges the digits onto the badge's centre. Chrome snaps glyphs to whole device
        // pixels, so this moves in steps, not smoothly: measured 9/29 in headless Chrome,
        // 0.75px centres them at 1x and 1.25x and is within ~0.4px at 1.5x/2x, where
        // 1.75px and up sat ~0.7px low at every scale.
        paddingTop: '0.75px',
        paddingLeft: unread_message_count < 10 ? '0.25px' : '3.25px',
        paddingRight: unread_message_count < 10 ? 0 : '3px',
        flexShrink: 0,
    }),
    systemAutoReplyBadge: {
        borderRadius: '50%',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        width: '17px',
        height: '17px',
        flexShrink: 0,
    },
    groupedTourBadge: (is_grouped_group_tour_row) => ({
        borderRadius: "50%",
        fontWeight: '500',
        display: is_grouped_group_tour_row ? "inline-block" : "none",
        textAlign: 'center',
        width: '17px',
        minWidth: '17px',
        height: '17px',
        fontSize: '.85rem',
        color: cv('text', 'black'),
        backgroundColor: cv('tag-tan', '#FDF3E1'),
        lineHeight: '17px',
        position: "absolute",
        marginLeft: "29px",
        marginTop: "1px"
    }),
};
export default ChatList;
